 <template>
    <div class="container">
        <div class="header">
            <h1>Aqfer Universal Tag</h1>
            <div class="d-flex align-items-center mb-3">
                <div class="progress progress-sm flex-grow-1 me-3">
                    <div class="progress-bar progress-bar-step-green" role="progressbar"
                        :style="'width: ' + completionPercentage + '%'" :aria-valuenow="completionPercentage"
                        aria-valuemin="0" aria-valuemax="100"></div>
                </div>
                <div class="completion-text">
                    <span :class="['badge', completedSteps === 8 ? 'bg-success' : 'bg-primary']">{{ completionText }}
                        Steps</span>
                </div>
            </div>
            <p>Click on any section to expand and learn more</p>
        </div>
        <div class="accordion" id="accordionChecklist">
            <div class="accordion-item" data-section="1">
                <h2 class="accordion-header">
                    <div class="accordion-header-container">
                        <div class="step-indicator">
                            <div class="step-circle" :class="{ 'completed': stepCompletion.step1 }">
                                <span v-if="stepCompletion.step1" class="step-check">✓</span>
                                <span v-else class="step-number">1</span>
                            </div>
                        </div>
                        <button class="accordion-button" :class="{ 'collapsed': !openSections.section1 }" type="button"
                            @click="toggleSection(1)" :aria-expanded="openSections.section1"
                            aria-controls="collapseOne">
                            <span class="section-title">Section 1: Introduction</span>
                        </button>
                    </div>
                </h2>
                <div id="collapseOne" class="accordion-collapse collapse" :class="{ 'show': openSections.section1 }"
                    data-bs-parent="#accordionExample">
                    <div class="accordion-body">
                        <div class="highlight">
                            <strong>Welcome to Aqfer Universal Tag Setup:</strong> This automated provisioning tool will
                            guide you through the configuration of your Aqfer Universal Tag deployment. The process is
                            designed to be straightforward and will help you set up all necessary components for a
                            successful implementation.
                        </div>
                        <p>This tool will help you configure DNS settings, cookie preferences, data collection
                            parameters, privacy settings, and other essential components needed for your Aqfer Universal
                            Tag deployment.</p>
                        <p>Each section contains specific configuration options with default values that work for most
                            use cases. You can customize these settings based on your specific requirements and
                            infrastructure.</p>
                        <p>There are only a couple of <strong>required updates</strong> the you will need to make. Those
                            are in Section 2, and include your primary domain name, the subdomain that you will use for
                            hosting the tags, and the configuration of the your DNS.</p>
                        <p>With a few exceptions, the setup required is the same regardless of which of the numerous DNS
                            providers your organization uses. For the exceptions, detailed instructions are provided.
                        </p>
                        <p>Once you have setup your domain names and DNS, you can leave all of the other configuration
                            settings in the default values.</p>
                        <p>As always, if you have any questions or concerns, please reach your Client Success team via our
                            <a href="https://aqferinc.atlassian.net/servicedesk/customer/portal/1">Service Desk.</a>
                        </p>

                        <div class="section-navigation">
                            <div></div>
                            <button type="button" class="btn btn-primary"
                                @click="continueToSection(2)">Continue</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="accordion-item" data-section="2">
                <h2 class="accordion-header">
                    <div class="accordion-header-container">
                        <div class="step-indicator">
                            <div class="step-circle" :class="{ 'completed': stepCompletion.step2 }">
                                <span v-if="stepCompletion.step2" class="step-check">✓</span>
                                <span v-else class="step-number">2</span>
                            </div>
                        </div>
                        <button class="accordion-button"
                            :class="{ 'collapsed': !openSections.section2, 'disabled': !canOpenSection(2) }"
                            type="button" @click="toggleSection(2)" :aria-expanded="openSections.section2"
                            aria-controls="collapseTwo" :disabled="!canOpenSection(2)">
                            <span class="section-title">Section 2: Edge Hostname and DNS</span>
                        </button>
                    </div>
                </h2>
                <div id="collapseTwo" class="accordion-collapse collapse" :class="{ 'show': openSections.section2 }"
                    data-bs-parent="#accordionExample">
                    <div class="accordion-body">
                        <div class="highlight">
                            <p><strong>In this section:</strong> Aqfer's Universal Tag uses hostnames to route data for
                                collection.
                                Every tag requires a hostname. Below you will find the Edge Hostname and DNS settings
                                for the Universal Tag.
                                There are multiple options for configuring your DNS settings, described below.
                                Alternatively, a simple CNAME can be used. For comparisons and when each approach applies, see
                                <a target="_blank" rel="noopener noreferrer"
                                    href="https://help.aqfer.net/new-aqfer-aut-developers-guide/domain-configurations-in-aut"
                                    class="link-primary">DNS and domain methods for aUT</a>
                                in the Aqfer help center.</p>
                            <p><strong>It is important for Aqfer to know which DNS service provider you use. With some
                                    DNS providers (e.g., AWS Route 53, GCP, Cloud Flare), there are additional considerations
                                    for configuration.</strong> Step-by-step guidance for those cases is in the
                                <a target="_blank" rel="noopener noreferrer"
                                    href="https://help.aqfer.net/new-aqfer-aut-developers-guide/client-dns-configurations?from_search=226434890#implementation-overview-1"
                                    class="link-primary">client DNS configuration guide</a>.
                            </p>
                        </div>

                        <div class="notes-section">
                            <p><strong>Notes:</strong> Multiple tagging domains can be set up with different tagging
                                subdomains after this initial configuration is completed. This allows you to organize
                                your tagging infrastructure across different domains or subdomains as needed for your
                                specific use cases.</p>
                        </div>

                        <div class="form-section">
                            <p class="form-text">Enter your primary domain name (e.g., example.com) without the protocol
                                (http/https) or www prefix. This will be used to generate your tagging URL and DNS
                                records.</p>
                            <InputField type="text" name="domainName" label="Domain Name" placeholder="example.com"
                                :inlineField="true" colField="col-6 m-auto" v-model="formData.domainName" />
                            <p class="form-text mt-2">The subdomain prefix for your tagging URL. The default 'tag' will
                                create a URL like tag.example.com. You can customize this to match your existing naming
                                conventions or branding.</p>
                            <InputField type="text" name="tagSubdomain" label="Tag Subdomain" placeholder="tag"
                                :inlineField="true" colField="col-6 m-auto" v-model="formData.tagSubdomain" />

                            <div v-if="!hideSection2GenerateBtn" class="d-flex justify-content-end mt-3 mb-2">
                                <button type="button" class="btn btn-primary" @click="saveDomain()"
                                    :disabled="!step2Completed">
                                    Generate
                                </button>
                            </div>

                            <div class="url-display">
                                <label class="form-label">Your {{ domainList.domains.items.length ? "existing" : "" }}
                                    tagging
                                    URL:</label>
                                <div class="url-output" v-for="domain, index in domainList.domains.items" :key="index"
                                    id="taggingUrl">{{ domain.name }}</div>
                                <div v-if="!domainList.domains.items?.length" class="url-output" id="taggingUrl">
                                    {{ `${effectiveTagSubdomain}.${effectiveDomainName}` }}
                                </div>
                            </div>
                        </div>

                        <!-- DNS Method Selection -->
                        <div class="dns-method-section mt-3">
                            <p class="form-text mt-2">Select your preferred DNS configuration method. Each method has
                                different requirements and implications for your setup.</p>
                            <InputField type="select" name="dnsMethod" label="DNS Method" :options="dnsMethodOptions"
                                v-model="formData.dnsMethod" :inlineField="true" colField="col-6 m-auto" />
                        </div>

                        <!-- DNS Method Instructions -->
                        <div v-if="formData.dnsMethod === 'chaining'" class="dns-instructions">
                            <p>The preferred method is to use the chaining method with an ALIAS and a CNAME. For how
                                this pattern compares to other DNS options, see
                                <a target="_blank" rel="noopener noreferrer" class="link-primary"
                                    href="https://help.aqfer.net/new-aqfer-aut-developers-guide/domain-configurations-in-aut">DNS and domain methods for aUT</a>
                                in the Aqfer help center.</p>
                            <DNSDiagram :dns-method="formData.dnsMethod"
                                :domain-name="effectiveDomainName"
                                :tag-subdomain="effectiveTagSubdomain"
                                :edge-hostname="domainList.edgeHostname || 'e1.a00000-cXXX.edgekey.net'" />
                        </div>
                        <div v-if="formData.dnsMethod === 'cname-only'" class="dns-instructions">
                            <p>Using only a CNAME record is a simpler approach but may impact Safari cookie durability.
                                This method requires only one DNS record.</p>
                            <DNSDiagram :dns-method="formData.dnsMethod"
                                :domain-name="effectiveDomainName"
                                :tag-subdomain="effectiveTagSubdomain"
                                :edge-hostname="domainList.edgeHostname || 'e1.a00000-cXXX.edgekey.net'" />
                        </div>

                        <div v-if="formData.dnsMethod === 'aws-route53'" class="dns-instructions">
                            <p>AWS Route 53 requires special configuration for DNS chaining. Use ALIAS records instead
                                of CNAME records for the root domain.</p>
                            <p class="mb-0">Record patterns and screenshots for Route&nbsp;53 are in the
                                <a target="_blank" rel="noopener noreferrer" class="link-primary"
                                    href="https://help.aqfer.net/new-aqfer-aut-developers-guide/client-dns-configurations?from_search=226434890#implementation-overview-1">client DNS configuration guide</a>.
                            </p>
                            <DNSDiagram :dns-method="formData.dnsMethod"
                                :domain-name="effectiveDomainName"
                                :tag-subdomain="effectiveTagSubdomain"
                                :edge-hostname="domainList.edgeHostname || 'e1.a00000-cXXX.edgekey.net'" />
                        </div>

                        <div v-if="formData.dnsMethod === 'cloudflare-dns'" class="dns-instructions">
                            <p>CloudFlare DNS requires specific settings for proper DNS chaining. Ensure CNAME
                                flattening is disabled for this configuration.</p>
                            <p class="mb-0">Cloudflare-specific steps are covered in the
                                <a target="_blank" rel="noopener noreferrer" class="link-primary"
                                    href="https://help.aqfer.net/new-aqfer-aut-developers-guide/client-dns-configurations?from_search=226434890#implementation-overview-1">client DNS configuration guide</a>.
                            </p>
                            <DNSDiagram :dns-method="formData.dnsMethod"
                                :domain-name="effectiveDomainName"
                                :tag-subdomain="effectiveTagSubdomain"
                                :edge-hostname="domainList.edgeHostname || 'e1.a00000-cXXX.edgekey.net'" />
                        </div>

                        <div v-if="formData.dnsMethod === 'cloudflare-hosted'" class="dns-instructions">
                            <p>For CloudFlare hosted sites, special considerations apply. The site must be configured in
                                "DNS Only" mode to avoid conflicts.</p>
                            <p class="mb-0">Cloudflare-specific steps are covered in the
                                <a target="_blank" rel="noopener noreferrer" class="link-primary"
                                    href="https://help.aqfer.net/new-aqfer-aut-developers-guide/client-dns-configurations?from_search=226434890#implementation-overview-1">client DNS configuration guide</a>.
                            </p>
                            <DNSDiagram :dns-method="formData.dnsMethod"
                                :domain-name="effectiveDomainName"
                                :tag-subdomain="effectiveTagSubdomain"
                                :edge-hostname="domainList.edgeHostname || 'e1.a00000-cXXX.edgekey.net'" />
                        </div>

                        <div class="dns-info-section">
                            <div class="dns-item">
                                <div class="dns-label">Your Edge Hostname:</div>
                                <div class="dns-value">{{ domainList.edgeHostname || "e1.a00000-cXXX.edgekey.net" }}
                                </div>
                            </div>

                            <div class="dns-section-header">DNS Settings</div>
                            <p class="dns-info-note text-muted small mb-2">
                                Add these records based on your chosen DNS method (traffic routing).
                            </p>
                             <div class="dns-item" v-show="showCnameRecord">
                                <div class="dns-label">CNAME:</div>
                                <div class="dns-value">{{ cnameRecordValue }}</div>
                            </div>

                            <div class="dns-item" v-show="showAliasRecord">
                                <div class="dns-label">ALIAS:</div>
                                <div class="dns-value">{{ aliasRecordValue }}</div>
                            </div>

                           
                            <div v-if="domainValidationDetails" class="validation-details-checklist mt-3">
                                <div class="dns-section-header">Validation records</div>
                                <p class="dns-info-note text-muted small mb-2">
                                    To complete validation, add <strong>either</strong> the CNAME record <strong>or</strong> the TXT record below to your DNS (Only one validation method is required).
                                </p>


                                <div v-if="domainValidationDetails.cname_record_host || domainValidationDetails.cname_record_target"
                                    class="validation-record-block">
                                    <div class="validation-record-header">
                                        <span class="validation-record-title"><span class="text-muted small me-2">Option 1:</span>CNAME record (Host → Target)</span>
                                    </div>
                                    <div class="dns-item validation-record-row">
                                        <div class="validation-record-field">
                                            <span class="dns-label validation-record-label">Host</span>
                                            <div class="dns-value dns-value-with-action">
                                                <span class="dns-text font-monospace">{{ domainValidationDetails.cname_record_host || '-' }}</span>
                                                <button type="button" class="btn btn-sm btn-outline-secondary"
                                                    :disabled="!domainValidationDetails.cname_record_host"
                                                    @click="copyText(domainValidationDetails.cname_record_host, 'CNAME host')" title="Copy">
                                                    <i class="fa fa-copy" />
                                                </button>
                                            </div>
                                        </div>
                                        <span class="validation-arrow"><i class="fa fa-arrow-right" /></span>
                                        <div class="validation-record-field">
                                            <span class="dns-label validation-record-label">Target</span>
                                            <div class="dns-value dns-value-with-action">
                                                <span class="dns-text font-monospace">{{ domainValidationDetails.cname_record_target || '-' }}</span>
                                                <button type="button" class="btn btn-sm btn-outline-secondary dns-action-btn"
                                                    :disabled="!domainValidationDetails.cname_record_target"
                                                    @click="copyText(domainValidationDetails.cname_record_target, 'CNAME target')" title="Copy">
                                                    <i class="fa fa-copy" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="domainValidationDetails.txt_record_host || domainValidationDetails.txt_record_value"
                                    class="validation-record-block">
                                    <div class="validation-record-header">
                                        <span class="validation-record-title"><span class="text-muted small me-2">Option 2:</span>TXT record (Host → Value)</span>
                                    </div>
                                    <div class="dns-item validation-record-row">
                                        <div class="validation-record-field">
                                            <span class="dns-label validation-record-label">Host</span>
                                            <div class="dns-value dns-value-with-action">
                                                <span class="dns-text font-monospace">{{ domainValidationDetails.txt_record_host || '-' }}</span>
                                                <button type="button" class="btn btn-sm btn-outline-secondary dns-action-btn"
                                                    :disabled="!domainValidationDetails.txt_record_host"
                                                    @click="copyText(domainValidationDetails.txt_record_host, 'TXT host')" title="Copy">
                                                    <i class="fa fa-copy" />
                                                </button>
                                            </div>
                                        </div>
                                        <span class="validation-arrow"><i class="fa fa-arrow-right" /></span>
                                        <div class="validation-record-field">
                                            <span class="dns-label validation-record-label">Value</span>
                                            <div class="dns-value dns-value-with-action">
                                                <span class="dns-text font-monospace">{{ domainValidationDetails.txt_record_value || '-' }}</span>
                                                <button type="button" class="btn btn-sm btn-outline-secondary dns-action-btn"
                                                    :disabled="!domainValidationDetails.txt_record_value"
                                                    @click="copyText(domainValidationDetails.txt_record_value, 'TXT value')" title="Copy">
                                                    <i class="fa fa-copy" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="dns-item" v-if="domainValidationDetails.validation_expiry">
                                    <div class="dns-label">Validation expiry</div>
                                    <div class="dns-value dns-value-with-action">
                                        <span class="dns-text font-monospace">{{ formatValidationExpiry(domainValidationDetails.validation_expiry) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="caaFound" class="checkbox-container">
                            <InputField type="checkbox" name="caaFound" label="CAA Found" v-model="caaFound"
                                :editable="false" />
                        </div>

                        <div id="csrContent" class="card p-5" v-show="caaFound">
                            <h3>Certificate Signing Request</h3>
                            <div class="">
                                <p>We have detected CAA (Certification Authority Authorization) record on your domain.
                                    In order for your tagging traffic to be protected by secure encryption, you will
                                    need to generate an SSL certificate and upload it. The form below can be used to
                                    start the process by generating a Certificate Signing Request (CSR).</p>

                                <p>A Certificate Signing Request (CSR) is a block of encoded text that contains
                                    information about your organization and the domain for which you're requesting an
                                    SSL certificate. When you submit a CSR to a Certificate Authority (CA), they use the
                                    information in it to create your SSL certificate. The CSR contains your public key
                                    and is signed with your private key, ensuring that only you can use the resulting
                                    certificate.</p>
                            </div>
                            <div class="card-body">
                                <InputField type="text" name="subjectAlternativeName"
                                    label="Subject Alternative Name (SAN)" :editable="false" :modelValue="domainName"
                                    :inlineField="true" colField="col-10" />
                                <InputField type="text" name="commonName" label="Common Name (CN)"
                                    placeholder="Enter common name" v-model="commonName" :inlineField="true"
                                    colField="col-10" :editable="!csrGenerated"
                                    :validate="getValidation('common name')" />
                                <br>
                                <h3 class="text-center">Admin Details</h3>
                                <InputField type="text" name="email" label="Email Address" placeholder="Enter email"
                                    v-model="admin.email" :inlineField="true" colField="col-10"
                                    :editable="!csrGenerated" :validate="getValidation('email')" />

                                <InputField type="number" name="phone" label="Phone" placeholder="Enter phone"
                                    v-model="admin.phone" :inlineField="true" colField="col-10"
                                    :editable="!csrGenerated" :validate="getValidation('phone')" />

                                <InputField type="text" name="firstName" label="First Name"
                                    placeholder="Enter first name" v-model="admin.firstName" :inlineField="true"
                                    colField="col-10" :editable="!csrGenerated"
                                    :validate="getValidation('first name')" />

                                <InputField type="text" name="lastName" label="Last Name" placeholder="Enter last name"
                                    v-model="admin.lastName" :inlineField="true" colField="col-10"
                                    :editable="!csrGenerated" :validate="getValidation('last name')" />

                                <br>
                                <h3 class="text-center">Organization Details</h3>
                                <InputField type="text" name="organization" label="Organization (O)"
                                    placeholder="Enter organization name" v-model="org.organizationName"
                                    :inlineField="true" :editable="!csrGenerated" colField="col-10"
                                    :validate="getValidation('organization name')" />

                                <InputField type="text" name="addressLineOne" label="Address"
                                    placeholder="Enter address" v-model="org.addressLineOne" :inlineField="true"
                                    colField="col-10" :editable="!csrGenerated"
                                    :validate="getValidation('address')" />

                                <InputField type="text" name="locality" label="Locality (L)"
                                    placeholder="Enter city/locality" v-model="org.locality" :editable="!csrGenerated"
                                    :inlineField="true" colField="col-10" :validate="getValidation('locality')" />
                                <InputField type="text" name="state" label="State/Province (ST)"
                                    placeholder="Enter state or province" v-model="org.state" :editable="!csrGenerated"
                                    :inlineField="true" colField="col-10" :validate="getValidation('state')" />

                                <InputField type="text" name="country" label="Country (C)"
                                    placeholder="Enter country code (e.g., US)" v-model="org.country"
                                    :inlineField="true" :editable="!csrGenerated" colField="col-10"
                                    :validate="getValidation('country')" />
                                <InputField type="text" name="postalCode" label="Postal Code"
                                    placeholder="Enter postal Code" v-model="org.postalCode" :inlineField="true"
                                    colField="col-10" :editable="!csrGenerated"
                                    :validate="getValidation('postal code')" />

                            </div>

                            <div class="text-center">
                                <button
                                    v-if="!csrGenerated || deploymentStatus === 'verify-third-party-cert-request' || deploymentStatus === 'pre-verification-safety-checks'"
                                    type="button" id="generateCsrBtn" @click="onGenerateCSR" class="btn btn-primary"
                                    :disabled="creating">
                                    <span v-if="creating || deploymentStatus === 'verify-third-party-cert-request'"
                                        class="spinner-border spinner-border-sm me-2" role="status"
                                        aria-hidden="true"></span>
                                    {{ creating ? 'Generating...' : deploymentStatus ===
                                        'verify-third-party-cert-request'
                                        || deploymentStatus === 'pre-verification-safety-checks' ? 'Verifying...' :
                                        'Generate CSR' }}
                                </button>
                            </div>
                            <br>
                            <div class="text-center"
                                v-if="csrGenerated && deploymentStatus === 'wait-upload-third-party'">
                                <button type="button" id="getCsr" class="btn btn-success me-3"
                                    @click="this.showModal({ component: 'GetCertificateCSR', data: this.certDeploy })">
                                    Get CSR
                                </button>
                                <button type="button" id="generateCsrBtn" class="btn btn-primary"
                                    @click="this.showModal({ component: 'UpdateSignedCertificate', data: { ...this.certDeploy, onSuccess: this.onCertificateUploadSuccess } })">
                                    Upload Signed Certificate
                                </button>
                            </div>
                        </div>


                        <div class="section-navigation" id="section2Continue">
                            <button type="button" class="btn btn-primary" @click="goBackToSection(1)">Back</button>
                            <div></div>
                            <button :disabled="!step2Completed" type="button" class="btn btn-primary"
                                @click.prevent="onValidateAndContinue">Validate & Continue</button>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <div class="accordion-item" data-section="3">
            <h2 class="accordion-header">
                <div class="accordion-header-container">
                    <div class="step-indicator">
                        <div class="step-circle" :class="{ 'completed': stepCompletion.step3 }">
                            <span v-if="stepCompletion.step3" class="step-check">✓</span>
                            <span v-else class="step-number">3</span>
                        </div>
                    </div>
                    <button class="accordion-button"
                        :class="{ 'collapsed': !openSections.section3, 'disabled': !canOpenSection(3) }" type="button"
                        @click="toggleSection(3)" :aria-expanded="openSections.section3" aria-controls="collapseThree"
                        :disabled="!canOpenSection(3)">
                        <span class="section-title">Section 3: Cookies</span>
                    </button>
                </div>
            </h2>
            <div id="collapseThree" class="accordion-collapse collapse" :class="{ 'show': openSections.section3 }"
                data-bs-parent="#accordionExample">
                <div class="accordion-body">


                    <div class="highlight">
                        <p><strong>In this section:</strong> Aqfer Universal Tag uses cookies as a part of data
                            collection. The options below have been configured with default values. In most use
                            cases, there is no need to change these settings.</p>
                        <p>Decisions around Aqfer Universal Tag cookie naming and additional cookie collection,
                            should be made in collaboration with stakeholders who understand both your technical
                            infrastructure and media strategy.</p>
                    </div>

                    <div class="notes-section">
                        <p><strong>Notes:</strong> If you will be tagging multiple domains with a first-party tag,
                            enter the first one here. You can add the additional domains after the initial
                            configuration is completed.</p>
                    </div>

                    <div class="cookie-config-section">
                        <InputField type="text" name="cookieName" label="Cookie Name" placeholder="Enter cookie name"
                            helpText="If you have an existing first-party cookies, you may want to change the cookie name to match existing cookies, thus continuing to build on your cookie pool. You can also change the name to align with branding."
                            v-model="generalSettings.cookieName" />

                        <InputField v-if="showCookieDomainField" type="select" name="cookieDomain" label="Cookie Domain"
                            :options="cookieDomainOptions"
                            :editable="isCookieConfigEditable"
                            helpText="The domain for which the cookie will be set. This should typically match your primary domain name."
                            v-model="generalSettings.cookieDomain" />

                        <div v-if="!isCookieConfigEditable" class="alert alert-info cookie-domain-note py-2 mb-3">
                            <i class="fa fa-info-circle me-1"></i>
                            If Cookie Domain updates are required, please contact an Aqfer Administrator to request these updates.
                        </div>

                        <InputField type="multiselect" name="cookieList" label="Log Additional Cookies"
                            placeholder="Enter additional cookies to log"
                            :helpText="`List additional 1st-party cookie names to capture (e.g. '_gaid'). A standalone '*' is not allowed—use explicit names. By default, only the cookie set by aUT (${generalSettings.cookieName}) is captured.`"
                            v-model="generalSettings.cookieList" :options="generalSettings.cookieList" />
                    </div>

                    <div class="opt-out-config-section">
                        <InputField v-if="showOptOutCookieNameField" type="text" name="optOutCookieName" label="Opt-Out Cookie Name"
                            placeholder="Enter opt-out cookie name"
                            :editable="isCookieConfigEditable"
                            helpText="Cookie opt-out functionality allows users to disable tracking by setting a specific cookie. When this cookie is present with the specified value, the Aqfer Universal Tag will respect the user's choice and cease data collection for that user."
                            v-model="optoutCookie.name" />

                        <InputField v-if="showOptOutCookieValueField" type="text" name="optOutCookieValue" label="Opt-Out Cookie Value"
                            placeholder="Enter opt-out cookie value"
                            :editable="isCookieConfigEditable"
                            helpText="The value that the opt-out cookie should contain to indicate that the user has chosen to opt out of tracking."
                            v-model="optoutCookie.value" />

                        <div v-if="!isCookieConfigEditable" class="alert alert-info opt-out-config-note py-2 mb-0">
                            <i class="fa fa-info-circle me-1"></i>
                            If Opt-Out Cookie Parameters are required, please contact an Aqfer Administrator to request these updates.
                        </div>
                    </div>

                    <div class="section-navigation">
                        <button type="button" class="btn btn-primary" @click="goBackToSection(2)">Back</button>
                        <button type="button" class="btn btn-primary" @click="continueToSection(4)">Continue</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="accordion-item" data-section="4">
            <h2 class="accordion-header">
                <div class="accordion-header-container">
                    <div class="step-indicator">
                        <div class="step-circle" :class="{ 'completed': stepCompletion.step4 }">
                            <span v-if="stepCompletion.step4" class="step-check">✓</span>
                            <span v-else class="step-number">4</span>
                        </div>
                    </div>
                    <button class="accordion-button"
                        :class="{ 'collapsed': !openSections.section4, 'disabled': !canOpenSection(4) }" type="button"
                        @click="toggleSection(4)" :aria-expanded="openSections.section4" aria-controls="collapseFour"
                        :disabled="!canOpenSection(4)">
                        <span class="section-title">Section 4: Privacy Settings</span>
                    </button>
                </div>
            </h2>
            <div id="collapseFour" class="accordion-collapse collapse" :class="{ 'show': openSections.section4 }"
                data-bs-parent="#accordionExample">
                <div class="accordion-body">


                    <div class="highlight">
                        <p><strong>In this section:</strong> Aqfer Universal Tag’s first-party tag is designed with
                            privacy and compliance at its foundation, leveraging the IAB Tech Lab’s Global Privacy
                            Platform (GPP) to support a wide range of regulatory frameworks.</p>
                        <p>GPP provides a standardized, flexible mechanism for transmitting user privacy and consent
                            signals across the digital advertising ecosystem. It simplifies compliance with both
                            global regulations like regional frameworks such as U.S. state privacy laws.
                        </p>
                        <p>By consolidating these diverse requirements into a single, extensible framework, GPP
                            ensures that organizations can consistently honor user choices while reducing the
                            complexity of managing multiple, fragmented implementations.</p>
                        <p>Aqfer enforces these privacy settings at the Akamai edge, ensuring that the most accurate
                            and jurisdictionally relevant signals are respected in real time, directly at the point
                            of interaction with the end user.</p>
                        <p>To configure U.S. privacy settings, determine whether requests from each
                            region should be allowed. By default, Aqfer Universal Tag blocks requests from EEA
                            countries and allows requests from the rest of the world.</p>
                        <p> For how GPP works with Universal Tag, see the
                            <a target="_blank" rel="noopener noreferrer"
                                href="https://help.aqfer.net/new-aqfer-aut-overview/gpp"
                                class="link-primary">GPP overview for aUT</a>
                            in the help center.</p>

                    </div>
                    <div class="notes-section">
                        <p><strong>Notes:</strong> This is client-level configuration; partner beacon setup can follow
                            later.
                        </p>
                    </div>
                    <div class="card">
                        <div class="card-header">
                            <h3>Global Privacy Platform (GPP)</h3>
                        </div>

                        <div class="card-body">
                            <p>Configure privacy compliance frameworks for different regions and jurisdictions.
                                Select the frameworks you need and configure their specific settings.</p>

                            <InputField v-if="showTcfSetting" type="checkbox" name="gppTcf" label="TCF (Transparency & Consent Framework)"
                                :editable="isGppConfigEditable" v-model="tcfVal" />

                            <InputField v-if="showUsPrivacySetting" type="checkbox" name="gppUsStates" label="US States Privacy Laws"
                                :editable="isGppConfigEditable" v-model="usMultiStatePrivacyVal" />

                            <div v-if="!isGppConfigEditable" class="alert alert-info gpp-config-note py-2 mb-0">
                                <i class="fa fa-info-circle me-1"></i>
                                If TCF or US States Privacy Law framework updates are required, please contact an Aqfer Administrator to request these updates.
                            </div>
                        </div>
                        <!-- TCF Framework Form -->
                        <div v-if="tcfVal" id="tcfForm" class="card-body">
                            <h4>TCF Configuration</h4>

                            <InputField type="radio" name="gpp.tcf.status" :showLabel="false"
                                :editable="isGppConfigEditable"
                                :options="privacyStatusOptions" v-model="tcf.status" />
                            <InputField v-if="tcf.status !== 'DISABLED'" type="number" name="tcfGvlId" label="TCF Gvl Id"
                                :editable="isGppConfigEditable"
                                helpText="TCF Global vendor List Identifier" v-model="tcf.gvlId" />

                        </div>

                        <!-- US States Framework Form -->
                        <div v-if="showUsStatesForm" id="usStatesForm" class="card-body">
                            <h4>US States Privacy Configuration</h4>
                            <div class="gpp-description">
                                <p>Configure privacy settings for each US state with privacy laws. First, set up a privacy config with signal mappings, then configure each state.</p>
                            </div>

                            <!-- Privacy Config Selection / Creation -->
                            <div class="card mb-3">
                                <div class="card-body">
                                    <h5 class="mb-3">Privacy Signal Configuration</h5>

                                    <!-- Show existing configs if available -->
                                    <div v-if="isGppConfigEditable && existingPrivacyConfigOptions.length > 0" class="mb-3">
                                        <label class="form-label fw-semibold">Use an existing config:</label>
                                        <div class="d-flex align-items-center gap-2">
                                            <InputField type="select" name="existingPrivacyConfig" :showLabel="false"
                                                :options="existingPrivacyConfigOptions" v-model="usMultiStatePrivacy.configName"
                                                placeholder="Select existing config" class="flex-grow-1 mb-0" />
                                            <button v-if="usMultiStatePrivacy.configName" type="button" class="btn btn-sm btn-outline-success"
                                                @click="selectExistingPrivacyConfig(usMultiStatePrivacy.configName)">
                                                <i class="fa fa-check me-1"></i>Use
                                            </button>
                                        </div>
                                    </div>

                                    <div v-if="isGppConfigEditable && existingPrivacyConfigOptions.length > 0" class="text-center text-muted my-2">
                                        <small>— OR create a new config —</small>
                                    </div>

                                    <!-- Create new config inline -->
                                    <div class="border rounded p-3">
                                        <div class="row mb-2">
                                            <div class="col-md-6">
                                                <InputField type="text" name="privacyConfigName" label="Config Name"
                                                    :placeholder="`${cid}_default`"
                                                    v-model="privacyConfigName" />
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-md-4">
                                                <InputField type="multiselect" name="privacySignals" label="Privacy Signals"
                                                    :toolTip="privacySignalInfo" :toolTipHtml="true"
                                                    :options="privacySignalOptions"
                                                    v-model="privacyConfigSignals.privacySignals" />
                                            </div>
                                            <div class="col-md-4">
                                                <InputField type="multiselect" name="childSensitiveDataSignals" label="Child Sensitive Data Signals"
                                                    :toolTip="childSensitiveSignalInfo" :toolTipHtml="true"
                                                    :options="childSensitiveSignalOptions"
                                                    v-model="privacyConfigSignals.childSensitiveDataSignals" />
                                            </div>
                                            <div class="col-md-4">
                                                <InputField type="multiselect" name="sensitiveDataProcessingSignals" label="Sensitive Data Processing Signals"
                                                    :toolTip="sensitiveDataSignalInfo" :toolTipHtml="true"
                                                    :options="sensitiveDataSignalOptions"
                                                    v-model="privacyConfigSignals.sensitiveDataProcessingSignals" />
                                            </div>
                                        </div>
                                        <div class="text-end mt-2">
                                            <button type="button" class="btn btn-primary btn-sm" @click="savePrivacyConfigInline()"
                                                :disabled="privacyConfigLoading">
                                                <span v-if="privacyConfigLoading" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else class="fa fa-save me-1"></i>Save Config
                                            </button>
                                        </div>
                                    </div>

                                    <div v-if="privacyConfigSaved" class="alert alert-success mt-2 mb-0 py-2">
                                        <i class="fa fa-check-circle me-1"></i>
                                        <template v-if="isGppConfigEditable">
                                            Privacy config "<strong>{{ usMultiStatePrivacy.configName }}</strong>" is active. You can now configure US state settings below.
                                        </template>
                                        <template v-else>
                                            Privacy config "<strong>{{ usMultiStatePrivacy.configName }}</strong>" is active. You can review the US state settings below. If US state setting updates are required, please contact an Aqfer Administrator to request these updates.
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <!-- US States Grid - only show after config is set -->
                            <div v-if="canProceedUsStates">
                                <div class="us-states-grid">
                                    <div v-for="state in usStates" :key="state.key" class="state-config">
                                        <InputField type="radio"
                                            :name="generalSettings['gpp.usMultiStatePrivacy' + state.key]"
                                            :label="state.label" :options="privacyStatusOptions"
                                            :editable="isGppConfigEditable"
                                            v-model="usMultiStatePrivacy[state.key]" />
                                    </div>
                                </div>
                            </div>
                            <div v-else class="alert alert-info">
                                <i class="fa fa-info-circle me-1"></i>
                                <template v-if="isGppConfigEditable">
                                    Please select or create a privacy config above before configuring US state settings.
                                </template>
                                <template v-else>
                                    Please create a privacy config above. If you need to use an existing config or update US state settings, please contact an Aqfer Administrator to request these updates.
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="section-navigation">
                        <button type="button" class="btn btn-primary" @click="goBackToSection(3)">Back</button>
                        <button type="button" class="btn btn-primary"
                            :disabled="usMultiStatePrivacyVal && !canProceedUsStates"
                            :title="usMultiStatePrivacyVal && !canProceedUsStates ? 'Please add a privacy config before continuing' : null"
                            @click="continueToSection(5)">Continue
                            <i v-if="usMultiStatePrivacyVal && !canProceedUsStates" class="fa fa-circle-exclamation ms-1"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="accordion-item" data-section="5">
            <h2 class="accordion-header">
                <div class="accordion-header-container">
                    <div class="step-indicator">
                        <div class="step-circle" :class="{ 'completed': stepCompletion.step5 }">
                            <span v-if="stepCompletion.step5" class="step-check">✓</span>
                            <span v-else class="step-number">5</span>
                        </div>
                    </div>
                    <button class="accordion-button"
                        :class="{ 'collapsed': !openSections.section5, 'disabled': !canOpenSection(5) }" type="button"
                        @click="toggleSection(5)" :aria-expanded="openSections.section5" aria-controls="collapseFive"
                        :disabled="!canOpenSection(5)">
                        <span class="section-title">Section 5: Users, Alerts and Notifications</span>
                    </button>
                </div>
            </h2>
            <div id="collapseFive" class="accordion-collapse collapse" :class="{ 'show': openSections.section5 }"
                data-bs-parent="#accordionExample">
                <div class="accordion-body">


                    <div class="highlight">
                        <strong>In this section:</strong> Configure user access and notification preferences for
                        your Aqfer Universal Tag deployment. Add team members who will receive alerts and
                        notifications about your tagging infrastructure.
                    </div>

                    <div class="row justify-content-end">
                        <div class="col-sm-3 d-flex position-absolute justify-content-end gap-3 add-user-row"
                            :class="[moveAddUserButton ? 'extra-margin-end' : '']">
                            <button type="button" id="addUserBtn" class="btn btn-primary" @click="openAddUserModal">
                                + Add User
                            </button>
                        </div>
                    </div>

                    <datatable tableId="usersTable-checklist" :columns="userColumns" uniqueField="email" :data="users"
                        @deleteRows="showDeleteUsers" :deletable="true" :compact="true" :selectCheckboxes="true"
                        :showLoading="false" :ignoreCache="true" @selectedRowCount="updateAddUserButtonPlacement" />

                    <div class="section-navigation">
                        <button type="button" class="btn btn-primary" @click="goBackToSection(4)">Back</button>
                        <button type="button" class="btn btn-primary" @click="continueToSection(6)">Continue</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="accordion-item" data-section="6">
            <h2 class="accordion-header">
                <div class="accordion-header-container">
                    <div class="step-indicator">
                        <div class="step-circle" :class="{ 'completed': stepCompletion.step6 }">
                            <span v-if="stepCompletion.step6" class="step-check">✓</span>
                            <span v-else class="step-number">6</span>
                        </div>
                    </div>
                    <button class="accordion-button"
                        :class="{ 'collapsed': !openSections.section6, 'disabled': !canOpenSection(6) }" type="button"
                        @click="toggleSection(6)" :aria-expanded="openSections.section6" aria-controls="collapseSix"
                        :disabled="!canOpenSection(6)">
                        <span class="section-title">Section 6: Activation</span>
                    </button>
                </div>
            </h2>
            <div id="collapseSix" class="accordion-collapse collapse" :class="{ 'show': openSections.section6 }"
                data-bs-parent="#accordionExample">
                <div class="accordion-body">


                    <div class="highlight">
                        <strong>In this section:</strong> The activation process involves sending your updated
                        configurations to Akamai for deployment across their global network. This ensures your Aqfer
                        Universal Tag settings are propagated to edge servers worldwide, enabling consistent data
                        collection and privacy compliance across all geographic regions.
                    </div>

                    <div class="card">
                        <div class="card-header">
                            <h4>Activation Overview</h4>
                        </div>
                        <div class="card-body">
                            <p>Once you complete the configuration and click "Activate", your settings will be
                                automatically deployed to Akamai's global content delivery network. The activation
                                process typically takes between 10-60 minutes, depending on the types of changes made
                                and current workload at Akamai.</p>

                            <h4>What Happens During Activation</h4>
                            <ul>
                                <li>Your configuration is validated and processed by the Aqfer system</li>
                                <li>Settings are securely transmitted to Akamai's deployment infrastructure</li>
                                <li>Configuration updates are propagated across Akamai's global edge network</li>
                                <li>Edge servers are updated with your new tag settings and privacy configurations</li>
                                <li>DNS and routing configurations are updated as needed</li>
                            </ul>
                        </div>
                        <div class="card-header">
                            <h4>Email Notifications</h4>
                        </div>
                        <div class="card-body">
                            <p>You will receive email notifications at the addresses specified in the "Notifications"
                                section:</p>
                            <ul>
                                <li><strong>Activation Commenced:</strong> Sent immediately when the deployment process
                                    begins</li>
                                <li><strong>Activation Complete:</strong> Sent when your configuration is fully deployed
                                    and active across the global network</li>
                            </ul>
                        </div>
                        <div class="card-header">
                            <h4>Expected Timeline</h4>
                        </div>
                        <div class="card-body">
                            <p><strong>Standard Changes:</strong> 10-30 minutes (cookie settings, privacy
                                configurations, basic tag modifications)</p>
                            <p><strong>Complex Changes:</strong> 30-60 minutes (DNS modifications, SSL certificate
                                updates, major configuration overhauls)</p>
                            <p><strong>Peak Hours:</strong> Activation may take longer during high-traffic periods
                                or maintenance windows</p>
                            <div class="row mt-4">
                                <div class="col-4">
                                    <span v-if="autdeploymentStatus"><strong>Status :</strong>
                                        <span>
                                            <span v-if="autdeploymentStatus.status === 'COMPLETED'"
                                                class="text-success">●</span>
                                            <span v-else-if="autdeploymentStatus.status === 'IN_PROGRESS'"
                                                class="text-warning">●</span>
                                            <span v-else-if="autdeploymentStatus.status === 'ABORTED'"
                                                class="text-danger">●</span>
                                            <span v-else class="text-primary">●</span>
                                            {{ formatStatusText(autdeploymentStatus.status) }}
                                        </span></span> &nbsp;
                                </div>
                                <div class="col-8 text-end">
                                    <button class="btn btn-sm btn-outline-secondary" v-if="showRefreshButton"
                                        @click="onRefreshClick">
                                        <i class="fa fa-refresh"></i>
                                    </button>&nbsp;
                                    <button type="button" class=" btn btn-primary"
                                        :disabled="!enableActivateButton || isActivating" @click="onActionBtnClick(7)">
                                        Activate
                                        <div v-if="!enableActivateButton" class="spinner-border spinner-border-sm"
                                            role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                        <i v-else-if="needActivation" class="fa fa-circle-exclamation text-warning"
                                            title="Client config changes have not been deployed. Activate to deploy the changes!"
                                            data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="section-navigation">
                        <button type="button" class="btn btn-primary" @click="goBackToSection(5)">Back</button>
                        <button type="button" class="btn btn-primary"
                            :disabled="needActivation || (autdeploymentStatus?.status !== 'COMPLETED')"
                            @click="continueToSection(7)"
                            :title="needActivation ? 'Please activate your configuration before continuing' : null"
                            data-bs-toggle="tooltip">
                            Continue
                            <i v-if="needActivation" class="fa fa-circle-exclamation ms-1"
                                title="Activation required to continue"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="accordion-item" data-section="7">
            <h2 class="accordion-header">
                <div class="accordion-header-container">
                    <div class="step-indicator">
                        <div class="step-circle" :class="{ 'completed': stepCompletion.step7 }">
                            <span v-if="stepCompletion.step7" class="step-check">✓</span>
                            <span v-else class="step-number">7</span>
                        </div>
                    </div>
                    <button class="accordion-button"
                        :class="{ 'collapsed': !openSections.section7, 'disabled': !canOpenSection(7) }" type="button"
                        @click="toggleSection(7)" :aria-expanded="openSections.section7" aria-controls="collapseSeven"
                        :disabled="!canOpenSection(7)">
                        <span class="section-title">Section 7: Data Collection, Logging & Tagging</span>
                    </button>
                </div>
            </h2>
            <div id="collapseSeven" class="accordion-collapse collapse" :class="{ 'show': openSections.section7 }"
                data-bs-parent="#accordionExample">
                <div class="accordion-body">


                    <div class="highlight">
                        <strong>In this section:</strong> Configure data collection settings and fine-tune how your
                        site, app and paid media collect
                        analytics, identity, and marketing signals. Optionally, customize examples for your existing
                        tag manager. You can choose the events to capture and examples will be created for you.
                        <p>Aqfer Universal Tag supports both 1st and 3rd party tags and offers options that control what
                            browser level data is collected.</p>
                        <p>First-party tags are code loaded from the same domain the user is visiting. They can set
                            and read first-party cookies, making them more durable, browser-friendly, and
                            privacy-compliant. Common uses include site analytics, consent management, and audience
                            tracking.</p>
                        <p>Third-party tags come from a different domain, often an ad network or external provider.
                            They rely on third-party cookies for cross-site tracking, but modern browsers and
                            privacy rules now block or restrict them heavily.</p>
                        <p>Sample page for tag fires (saved tagging host when available, otherwise Section 2 draft):
                            <a v-if="sampleTagFirePageUrl" target="_blank" rel="noopener noreferrer"
                                :href="sampleTagFirePageUrl" class="link-primary">Open sample page</a>
                            <span v-else class="text-muted">Save a tagging domain in Section 2 or enter domain and tag
                                subdomain there to build this link.</span>
                        </p>
                        <p>For how first-party tagging URLs are built—paths, parameters, and placeholder tokens—see
                            <a target="_blank" rel="noopener noreferrer"
                                href="https://help.aqfer.net/new-aqfer-aut-developers-guide/new-aqfer-tag-url-format"
                                class="link-primary">Universal Tag URL format</a>
                            in the Aqfer help center.</p>
                    </div>

                    <div class="notes-section">
                        <p><strong>Notes:</strong> For a complete list of all browser data collected, and example
                            values, view our knowledge base at:
                            <a target="_blank" rel="noopener noreferrer" class="link-primary"
                                href="https://help.aqfer.net/en_US/new-aqfer-aut-developers-guide/log-record-schema#avro-schema-2">https://help.aqfer.net/en_US/new-aqfer-aut-developers-guide/log-record-schema#avro-schema-2</a>
                        </p>
                    </div>

                    <!-- Data Collection and Logging Configuration -->
                    <div class="card p-3 mb-4">
                        <h4>Data Collection and Logging Configuration</h4>
                        <p class="text-muted">By default, we will use the <strong>Extended</strong>
                            logging format, which includes geolocation, network and browser header data. In addition, we
                            will collect the browsers <strong>IP Address</strong> as a hashed value.</p>

                        <InputField type="radio" name="logFormat" label="Log Format"
                            helpText="Use Extended format if you wish to automatically collect geolocation and network signals."
                            :options="logFormatOptions" v-model="formData.logFormat" />

                        <InputField type="radio" name="logDelivery" label="Log Delivery Method"
                            helpText="Log Delivery Method has no effect in terms of data delivery"
                            :options="logDeliveryOptions" v-model="formData.logDelivery" />

                        <InputField type="radio" name="ipCollection" label="IP Address Collection"
                            helpText="The IP Address of the browser can be logged in either plain text (e.g., 192.168.1.1), hashed for privacy, or not logged at all."
                            :options="ipCollectionOptions" v-model="formData.ipCollection" />

                        <div class="card-footer">
                            <div>Calculated Logging Mode: <span><em>{{ calculateLoggingMode.toUpperCase() }}</em></span>
                            </div>
                        </div>
                    </div>

                    <div class="notes-section">
                        <p><strong>Note:</strong> In the example tags below, there will usually be placeholders
                            used. A placeholder is used when a parameter should be replaced when trafficking the tag
                            or dynamically replaced at run time. Placeholders will be surrounded by angle brackets
                            and should be replaced in their entirety including the brackets.
                        </p>
                    </div>


                    <!-- Owned & Operated Sites Section -->
                    <div class="card">
                        <div class="card-header">
                            <h4>Owned & Operated Sites</h4>
                        </div>
                        <!-- Initiators Subsection -->
                        <div class="card-body">
                            <h4>Initiators</h4>
                            <p class="text-muted">If you are using an existing system to deploy tags to your sites
                                (e.g., Google Tag Manager), select it here. With this information, we will generate tags
                                with appropriate macro substitution.</p>
                            <InputField type="radio" name="initiator" :showLabel="false" :options="initiatorOptions"
                                v-model="initiator" />
                        </div>
                        <div class="card-body mt-4">
                            <!-- Tag Type Configuration -->
                            <h4>Tag Type Configuration</h4>
                            <p class="text-muted">Configure how your tracking tags will be delivered and executed.
                            </p>

                            <InputField type="radio" name="deliveryType" label="Delivery Type"
                                helpText="Choose between atomic (single tag) or container (wrapper) delivery methods."
                                :options="deliveryOptions" v-model="deliveryType" />

                            <div v-if="deliveryType === 'atomic'" class="mt-3">
                                <InputField type="radio" name="atomicType" label="Atomic Type"
                                    helpText="Select the type of atomic tag implementation." :options="atomicOptions"
                                    v-model="atomicType" />
                            </div>

                            <div v-if="deliveryType !== 'atomic' || atomicType !== 'r'" class="mt-3">
                                <InputField type="radio" name="logicType" label="Extra Logic"
                                    helpText="Add additional functionality to your tags." :options="logicOptions"
                                    v-model="logicType" />
                            </div>

                            <div class="mt-3">
                                <InputField type="text" name="calculatedTagType" label="Calculated Tag Type"
                                    helpText="This is automatically calculated based on your selections above."
                                    :editable="false" v-model="calculatedTagType" />
                            </div>
                        </div>

                        <div class="card-body">
                            <!-- Event Types Subsection -->
                            <h4>Event Types</h4>
                            <p class="text-muted">Select which event types you will be collecting for your
                                owned, operated sites or paid media campaigns.</p>
                            <InputField type="radio" name="eventType" :showLabel="false" :options="allEventTypeOptions"
                                v-model="eventType" />

                            <!-- Sub Event Type 1 (dynamically added) -->
                            <div v-if="subEventOptions1.length > 0" class="mt-3">
                                <InputField type="radio" name="subEventType1" label="Sub Event Type 1"
                                    helpText="Select the specific sub-type for your event." :options="subEventOptions1"
                                    v-model="subEventType1" />
                            </div>

                            <!-- Sub Event Type 2 (dynamically added) -->
                            <div v-if="subEventOptions2.length > 0" class="mt-3">
                                <InputField type="radio" name="subEventType2" label="Sub Event Type 2"
                                    helpText="Select the more specific sub-type for your event."
                                    :options="subEventOptions2" v-model="subEventType2" />
                            </div>
                        </div>
                        <div class="card-body mt-n4">
                            <h4>Generated Tag Code</h4>
                            <div class="row mb-2">
                                <div class="col-md-4 py-0">
                                    <InputField type="select" name="tagDomain" label="Choose Domain"
                                        :options="cookieDomainOptions" v-model="tagDomain"
                                        placeholder="Select a domain" />
                                </div>
                            </div>
                            <div v-if="safariItpApplies" class="row mb-2">
                                <div class="col-md-4 py-0">
                                    <InputField type="text" name="safariItpClientDomain"
                                        label="Client Website Domain" placeholder="ex: www.example.com"
                                        :validate="[{ type: 'required' }]"
                                        toolTip="Your own website domain that proxies through to aUT. Safari and iOS traffic is served from this domain instead of the aUT domain, so Safari does not truncate the aUT cookie."
                                        v-model="safariItpClientDomain" />
                                </div>
                                <div class="col-md-4 py-0">
                                    <InputField type="text" name="safariItpPathPrefix" label="Path Prefix"
                                        placeholder="ex: /proxy" :validate="[{ type: 'required' }]"
                                        toolTip="Path prefix prepended to the aUT request path on your website domain."
                                        v-model="safariItpPathPrefix" />
                                </div>
                            </div>
                            <div v-if="safariItpIncomplete" class="card-footer rounded-3 text-muted">
                                Enter the Client Website Domain and Path Prefix to generate the tag for
                                <strong>{{ tagDomain }}</strong>. Safari and iOS traffic is routed through
                                your own website domain, so the tag cannot be generated without them.
                            </div>
                            <div v-else class="card-footer rounded-3">
                                <code class=" html xml">
                                        {{ generatedCode }}
                                    </code>
                            </div>
                            <button v-if="generatedCode && !safariItpIncomplete"
                                class="btn btn-primary float-end mt-2"
                                @click="copyGeneratedCode()">
                                Copy Code
                            </button>
                        </div>
                    </div>
                    <div class="section-navigation">
                        <button type="button" class="btn btn-primary" @click="goBackToSection(6)">Back</button>
                        <button type="button" class="btn btn-primary" @click="openTagModal">Save Tag Template</button>
                        <button type="button" class="btn btn-primary" @click="continueToSection(8)">Continue</button>
                    </div>
                </div>
            </div>
        </div>


        <div class="accordion-item" data-section="8">
            <h2 class="accordion-header">
                <div class="accordion-header-container">
                    <div class="step-indicator">
                        <div class="step-circle" :class="{ 'completed': stepCompletion.step8 }">
                            <span v-if="stepCompletion.step8" class="step-check">✓</span>
                            <span v-else class="step-number">8</span>
                        </div>
                    </div>
                    <button class="accordion-button"
                        :class="{ 'collapsed': !openSections.section8, 'disabled': !canOpenSection(8) }" type="button"
                        @click="toggleSection(8)" :aria-expanded="openSections.section8" aria-controls="collapseEight"
                        :disabled="!canOpenSection(8)">
                        <span class="section-title">Section 8: Next Steps… Prepare to Review Collected Data</span>
                    </button>
                </div>
            </h2>
            <div id="collapseEight" class="accordion-collapse collapse" :class="{ 'show': openSections.section8 }"
                data-bs-parent="#accordionExample">
                <div class="accordion-body">


                    <div class="highlight">
                        <strong>In this section:</strong> Learn how your collected data flows into the Aqfer data
                        lakehouse, how standard data marts are created for analysis, and how to access and query
                        your data using AWS Athena and other analytical tools.
                    </div>

                    <div class="data-lakehouse-section">
                        <h3>Data Lakehouse Architecture</h3>
                        <p>Your Aqfer Universal Tag data is automatically ingested into our cloud-based data
                            lakehouse, providing a scalable and cost-effective foundation for analytics. The
                            lakehouse architecture combines the best of data lakes and data warehouses, offering
                            both storage flexibility and query performance.</p>

                        <h4>Data Ingestion Process</h4>
                        <ul>
                            <li>
                                <a target="_blank" rel="noopener noreferrer" class="link-primary"
                                    href="https://help.aqfer.net/en_US/new-aqfer-aut-developers-guide/log-record-schema#avro-schema-2">Raw event</a>
                                data is collected from your tags and securely transmitted to the lakehouse
                            </li>
                            <li>Data is automatically partitioned by date and event type for optimal query
                                performance</li>
                            <li>Schema validation ensures data quality and consistency</li>
                            <li>Data is stored for efficient compression and optimized for further processing</li>
                        </ul>

                        <h4>Standard Data Mart Creation</h4>
                        <p>We automatically create a standard data mart with pre-built tables optimized for common
                            analytical use cases:</p>
                        <ul>
                            <li><strong>Hourly Collation Table:</strong> Aggregated by entity by hour for quick
                                monitoring and quality assurance</li>
                            <li><strong>Daily Collation Table:</strong> Daily rollups for trend analysis and
                                reporting</li>
                            <li><strong>Raw Event Table:</strong>Unprocessed log records provide lowest latency to
                                data visability</li>
                        </ul>
                        <p>To know more about the collation process
                            <a target="_blank" rel="noopener noreferrer"
                                href="https://help.aqfer.net/aut-amdp-bridge-docs/aut-amdp-bridge-starter-guide"
                                class="link-primary">Click here</a>
                        </p>
                        <h4>Querying Your Data with AWS Athena</h4>
                        <p>Access your data directly through the AWS Athena console using standard SQL queries.
                            Athena provides a serverless, interactive query service that makes it easy to analyze
                            data in the lakehouse.</p>

                        <div class="card">
                            <div class="card-header">
                                <h5>Sample Queries</h5>
                            </div>
                            <div class="card-body">
                                <div class="code-example">
                                    <code>-- Daily page view counts by domain
select
event_date,
sum(cardinality(views)) page_views,
sum(cardinality(impressions)) impressions,
sum(cardinality(conversions)) conversions,
sum(cardinality(id_syncs)) id_syncs
from
collation_daily_v6_3
where
event_date between '20250801' and '20250909'
group by 1
order by 1</code>
                                </div>

                                <div class="code-example">
                                    <pre><code>-- Top performing campaigns (last 30 days)
select
event_date,
sum(cardinality(views)) page_views,
sum(cardinality(impressions)) impressions,
sum(cardinality(conversions)) conversions,
sum(cardinality(id_syncs)) id_syncs
from
collation_daily_v6_3
where
event_date between '20250801' and '20250909'
group by 1
order by 1</code></pre>
                                </div>
                            </div>
                        </div>
                        <h4>PAIL Queries for Data Validation</h4>
                        <p>Use PAIL (Partitioned Analytics and Insights Language) queries to perform spot checks and
                            data validation:</p>
                        <ul>
                            <li><strong>Data Quality Checks:</strong> Verify data completeness and identify
                                anomalies</li>
                            <li><strong>Volume Monitoring:</strong> Track data ingestion rates and identify trends
                            </li>
                            <li><strong>Schema Validation:</strong> Ensure data structure consistency across
                                partitions</li>
                        </ul>

                        <h4>Connecting to Athena</h4>
                        <p>Set up ODBC or JDBC drivers to connect your preferred analytics tools directly to Athena:
                        </p>

                        <div class="card">
                            <div class="card-header">

                                <h5>Driver Setup Guides</h5>
                            </div>
                            <div class="card-body">
                                <ul>
                                    <li><a href="https://docs.aws.amazon.com/athena/latest/ug/connect-with-odbc.html"
                                            target="_blank" class="external-link">AWS Athena ODBC Driver Setup</a></li>
                                    <li><a href="https://docs.aws.amazon.com/athena/latest/ug/connect-with-jdbc.html"
                                            target="_blank" class="external-link">AWS Athena JDBC Driver Setup</a></li>
                                    <li><a href="https://docs.aws.amazon.com/athena/latest/ug/connect-with-bi-tools.html"
                                            target="_blank" class="external-link">Connecting BI Tools to Athena</a></li>
                                </ul>
                            </div>
                        </div>
                        <div class="card">
                            <div class="card-header">
                                <h5>Supported Analytics Tools</h5>
                            </div>
                            <div class="card-body">
                                <ul>
                                    <li>Tableau, Power BI, Looker, and other BI platforms</li>
                                    <li>Python (pandas, PyAthena), R, and other data science tools</li>
                                    <li>Jupyter Notebooks and Apache Zeppelin</li>
                                    <li>Custom applications using ODBC/JDBC connectors</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="section-navigation">
                        <button type="button" class="btn btn-primary" @click="goBackToSection(7)">Back</button>
                        <button type="button" class="btn btn-primary" @click="finish()">Finish</button>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>
<script>
import InputField from '../components/InputField.vue';
import { mapActions, mapState } from 'vuex';
import Datatable from '../components/Datatable.vue';
import { tag_event_types } from '../utils/tagChecklistOption.js';
import DNSDiagram from '@/components/DNSDiagram.vue';
import { deepEqual, DateTimeFormat } from "../utils/commonFunction";
import { isProxyEnabledDomain, safariItpTagParts } from "../utils/safariItp";
export default {
    name: 'ClientCheckList',
    components: {
        InputField,
        Datatable,
        DNSDiagram
    },
    data() {
        return {
            stepCompletion: {
                step1: false, // Introduction
                step2: false, // Edge Hostname and DNS
                step3: false, // Cookies
                step4: false, // Privacy Settings
                step5: false, // Users, Alerts and Notifications
                step6: false, // Activation
                step7: false, // Data Collection, Logging & Tagging
                step8: false, // Next Steps
            },
            openSections: {
                section1: false,
                section2: false,
                section3: false,
                section4: false,
                section5: false,
                section6: false,
                section7: false,
                section8: false
            },
            currentSection: 1,
            deliveryType: 'atomic',
            atomicType: 'img',
            logicType: '',
            domainList: { domains: { items: [] } },
            step2Completed: false,
            showAliasRecord: true,
            showCnameRecord: true,
            domainValidationDetails: null,
            hideSection2GenerateBtn: false,
            csrGenerated: false,
            csr: {},
            creating: false, // Loading state for certificate generation
            intervalId: null,
            intervalCount: 0,
            certDeploy: null, // Certificate Deployment Status
            caaFound: false,
            uploadedCertificate: false, // Flag to check if certificate is uploaded
            recordType: 'PROXY',
            tagDomain: '',
            safariItpClientDomain: '',
            safariItpPathPrefix: '',
            tcf: {
                status: "DISABLED",
                gvlId: null,
                mode: null
            },
            usMultiStatePrivacy: {
                "configName": null,
                "usnat": "DISABLED",
                "usca": "DISABLED",
                "usva": "DISABLED",
                "usco": "DISABLED",
                "usut": "DISABLED",
                "usct": "DISABLED",
                "usfl": "DISABLED",
                "usmt": "DISABLED",
                "usor": "DISABLED",
                "ustx": "DISABLED",
                "usde": "DISABLED",
                "usia": "DISABLED",
                "usne": "DISABLED",
                "usnh": "DISABLED",
                "usnj": "DISABLED",
                "ustn": "DISABLED",
                "usmn": "DISABLED",
                "usmd": "DISABLED",
                "usin": "DISABLED",
                "usky": "DISABLED",
                "usri": "DISABLED"


            },
            tcfVal: false,
            usMultiStatePrivacyVal: false,
            privacyConfigName: '',
            privacyConfigSignals: { privacySignals: [], childSensitiveDataSignals: [], sensitiveDataProcessingSignals: [] },
            privacyConfigSaved: false,
            privacyConfigLoading: false,
            admin: {},
            org: {},
            commonName: null,
            email: "",
            generalSettings: {
                cookieName: '',
                cookieDomain: '',
                cookieList: [],
            },
            snapShot: {},
            optoutCookie: {
                name: null,
                value: null
            },
            formData: {
                domainName: '',
                cookieDomain: '',
                tagSubdomain: '',
                dnsMethod: 'chaining',
                logFormat: 'extended',
                logDelivery: 'batch',
                ipCollection: 'hashed'
            },
            /** Committed when user clicks Generate; diagram/alias/cname use these so they update only after Generate */
            displayDomainName: false,
            initiator: '',
            initiatorFields: [],
            eventType: '',
            subEventType1: '',
            subEventType2: '',

            dnsMethodOptions: [
                { value: 'chaining', label: 'DNS Chaining (Preferred) - CNAME and ALIAS records' },
                { value: 'cname-only', label: 'CNAME only - Only a CNAME record, impacts Safari cookie durability' },
                { value: 'aws-route53', label: 'DNS Chaining w/AWS Route 53' },
                { value: 'cloudflare-dns', label: 'DNS with CloudFlare DNS' },
                { value: 'cloudflare-hosted', label: 'CloudFlare hosted sites (special)' }
            ],

            logFormatOptions: [
                { value: 'base', label: 'Base' },
                { value: 'extended', label: 'Extended (default)' }
            ],

            logDeliveryOptions: [
                { value: 'batch', label: 'Batch (default)' },
                { value: 'streaming', label: 'Streaming' }
            ],

            ipCollectionOptions: [
                { value: 'none', label: 'None' },
                { value: 'plain-text', label: 'Plain Text' },
                { value: 'hashed', label: 'Hashed (default)' }
            ],

            privacyStatusOptions: [
                { value: 'DISABLED', label: 'DISABLED' },
                { value: 'BLOCKED', label: 'BLOCKED' },
                { value: 'ENABLED', label: 'ENABLED' }
            ],

            deliveryOptions: [
                { value: 'atomic', label: 'Atomic' },
                { value: 'container', label: 'Container' }
            ],

            atomicOptions: [
                { value: 'img', label: 'Image' },
                { value: 'js', label: 'JavaScript' },
                { value: 'r', label: 'Redirect' }
            ],

            usStates: [
                { key: 'usnat', label: 'USNAT (National)' },
                { key: 'usca', label: 'USCA (California)' },
                { key: 'usva', label: 'USVA (Virginia)' },
                { key: 'usco', label: 'USCO (Colorado)' },
                { key: 'usut', label: 'USUT (Utah)' },
                { key: 'usct', label: 'USCT (Connecticut)' },
                { key: 'usfl', label: 'USFL (Florida)' },
                { key: 'usmt', label: 'USMT (Montana)' },
                { key: 'usor', label: 'USOR (Oregon)' },
                { key: 'ustx', label: 'USTX (Texas)' },
                { key: 'usde', label: 'USDE (Delaware)' },
                { key: 'usia', label: 'USIA (Iowa)' },
                { key: 'usne', label: 'USNE (Nebraska)' },
                { key: 'usnh', label: 'USNH (New Hampshire)' },
                { key: 'usnj', label: 'USNJ (New Jersey)' },
                { key: 'ustn', label: 'USTN (Tennessee)' },
                { key: 'usmd', label: 'USMD (Maryland)' },
                { key: 'usin', label: 'USIN (Indiana)' },
                { key: 'usky', label: 'USKY (Kentucky)' },
                { key: 'usri', label: 'USRI (Rhode Island)' },

            ],
            userColumns: [
                {
                    data: "name",
                    width: "25%"
                },
                {
                    data: "email",
                    width: "25%"
                },
                {
                    data: "roles",
                    width: "20%",
                    render: function (data, type, row) {
                        if (!data) return "";
                        return Array.isArray(data) ? data.join(" ") : data;
                    },
                },
                {
                    data: "alert",
                    title: "Alerts",
                    width: "15%"
                },
                {
                    data: "notification",
                    title: "Notifications",
                    width: "15%"
                }
            ],
            users: [],
            moveAddUserButton: false,
            isInitialDataLoad: true, // Flag to prevent change detection during initial data loading
            lastActivatedConfig: null, // Store last activated configuration for comparison
            isActivating: false, // Track activation in progress,
            isActivated: false
        };
    },
    watch: {
        domainName(newVal, oldVal) {
            if (newVal && newVal !== oldVal && newVal.length > 0) {
                this.checkCAA(newVal);
            }
            this.updateTaggingUrl();
        },
        'formData.dnsMethod'(newVal) {
            if (newVal === 'chaining') {
                this.recordType = 'PROXY';
            } else if (newVal === 'aws-route53') {
                this.recordType = 'FWD';
            } else if (newVal === 'cloudflare-dns' || newVal === 'cloudflare-hosted') {
                this.recordType = 'PRIVATE';
            } else {
                this.recordType = 'CNAME';
            }
            this.updateDnsValues();
        },
        /**
         * A stale client domain left behind by an earlier selection would otherwise end
         * up in the generated tag, so switching to a domain without proxy support
         * clears both Safari ITP values.
         */
        tagDomain() {
            this.clearSafariItpIfUnused();
        },
        effectiveTagType() {
            this.clearSafariItpIfUnused();
        },
        'formData.logFormat'() {
            this.updateBaseUrl();
        },

        'formData.logDelivery'() {
            this.updateBaseUrl();
        },

        'formData.ipCollection'() {
            this.updateBaseUrl();
        },

        eventType() {
            this.subEventType1 = '';
            this.subEventType2 = '';
        },

        subEventType1() {
            this.subEventType2 = '';
        },
        initiator() {
            this.initiatorFields = [];
            this.getInitiatorFields();
        },
        'generalSettings.cookieList': {
            deep: true,
            handler(val) {
                const cleaned = this.sanitizeCookieListEntries(val || []);
                if (Array.isArray(val) && cleaned.length !== val.length) {
                    this.generalSettings.cookieList = cleaned;
                    this.$store.dispatch('addToast', {
                        message: "A standalone '*' cannot be used here—enter each cookie name explicitly.",
                        type: 'warning'
                    });
                }
            }
        },
        tcfVal(){
            if(this.tcfVal){
                this.tcf.status = "ENABLED";
                this.tcf.gvlId = 1;
                this.tcf.mode = "DV_MANDATORY";
            } else {
                this.tcf.status = "DISABLED";
                this.tcf.gvlId = null;
                this.tcf.mode = null;
            }
        },
        usMultiStatePrivacyVal(newVal) {
            if (!newVal) {
                // Reset all US states to DISABLED and configName to null
                Object.keys(this.usMultiStatePrivacy).forEach(key => {
                    if (key !== 'configName') {
                        this.usMultiStatePrivacy[key] = 'DISABLED';
                    }
                });
                this.usMultiStatePrivacy.configName = null;
                this.privacyConfigSaved = false;
                this.privacyConfigName = '';
                this.privacyConfigSignals = { privacySignals: [], childSensitiveDataSignals: [], sensitiveDataProcessingSignals: [] };
            }
        }
    },
    computed: {
        ...mapState({
            autClientData: (state) => state.aut.autClientData,
            ownerEmail: (state) => state.currentUser.email,
            cid: (state) => state.currentClient.cid,
            autdeploymentStatus: state => state.aut.deploymentStatus,
            privacySignals: state => state.aut.privacySignals,
            privacyConfigs: state => state.aut.privacyConfig,
        }),
        completedSteps() {
            return Object.values(this.stepCompletion).filter(status => status).length;
        },
        completionPercentage() {
            return Math.round((this.completedSteps / 8) * 100);
        },
        completionText() {
            return `${this.completedSteps}/8`;
        },
        calculateLoggingMode() {
            const logFormat = this.formData.logFormat;
            const logDelivery = this.formData.logDelivery;
            const ipCollection = this.formData.ipCollection;

            // logging mode mapping
            const loggingModes = {
                // Batch modes
                'batch-base-hashed': 'b',
                'batch-base-plain': 'c',
                'batch-extended-hashed': 'd',
                'batch-extended-plain': 'e',
                'batch-base-none': 'j',
                'batch-extended-none': 'k',

                // Streaming modes
                'streaming-base-hashed': 'f',
                'streaming-base-plain': 'g',
                'streaming-extended-hashed': 'h',
                'streaming-extended-plain': 'i',
                'streaming-base-none': 'l',
                'streaming-extended-none': 'm'
            };

            // Default case (no logs)
            let selectedMode = 'a';

            // Build the key based on current selections
            if (logFormat && logDelivery && ipCollection) {
                const key = `${logDelivery}-${logFormat}-${ipCollection === 'plain-text' ? 'plain' : ipCollection}`;
                selectedMode = loggingModes[key] || selectedMode;
            }

            return selectedMode;
        },

        initiatorOptions() {
            const storeInitiators = this.$store.getters["aut/initiatorOptions"] || [];
            const options = [{ value: '', label: 'None' }];
            storeInitiators.forEach(initiator => {
                options.push({
                    value: initiator.value || initiator.name?.toLowerCase().replace(/\s+/g, '-'),
                    label: initiator.label || initiator.name,
                });
            });

            return options;
        },

        allEventTypeOptions() {
            return tag_event_types.map(eventType => ({
                value: eventType.value,
                label: eventType.label
            }));
        },

        subEventOptions1() {
            if (!this.eventType) return [];
            const selectedEvent = tag_event_types.find(event => event.value === this.eventType);
            return selectedEvent?.sub_events || [];
        },

        subEventOptions2() {
            if (!this.subEventType1 || this.subEventOptions1.length === 0) return [];

            const selectedSubEvent = this.subEventOptions1.find(subEvent => subEvent.value === this.subEventType1);
            return selectedSubEvent?.sub_events || [];
        },
        logicOptions() {
            var options = [];

            if (this.deliveryType != "atomic" || this.atomicType != "js")
                options.push({ label: "None", value: "" });

            options.push({ label: "Cookie", value: "c" });

            if (this.deliveryType != "atomic" || this.atomicType != "img")
                options.push(
                    { label: "Stat", value: "s" },
                    { label: "Cookie and Stat", value: "cs" }
                );

            return options;
        },

        generatedCode() {
            let template;
            let tagParams = {};

            // Add event type parameters (same as TagTemplate)
            if (this.eventType)
                tagParams["aqet"] = this.eventType;
            if (this.subEventType1)
                tagParams["aqet_l1"] = this.subEventType1;
            if (this.subEventType2)
                tagParams["aqet_l2"] = this.subEventType2;

            if (this.initiatorFields) {
                for (let i = 0; i < this.initiatorFields.length; i++) {
                    const field = this.initiatorFields[i];
                    tagParams[field.parameter] = field.input;
                }
            }

            const tagType = this.effectiveTagType;
            if (tagType === 'r') {
                tagParams['ru'] = '{{redirect_url}}';
            }

            const loggingMode = this.calculateLoggingMode;
            const clientLabel = this.autClientData?.label || 'aqfer';
            const hostname = this.tagDomainValue;

            if (!this.emitsScript) {
                // URL TEMPLATE - USE FOR ATOMIC IMG TAGS (same as TagTemplate)
                const urlParams = Object.keys(tagParams)
                    .map((k) => `${k}=${tagParams[k]}`)
                    .join("&");
                template = `https://${hostname}/1/${loggingMode}/${tagType}?${urlParams}`;
            } else {
                // WRAPPER TEMPLATE - ALL OTHER TAGS (exactly like TagTemplate)
                // Safari ITP: for a proxy-enabled domain the tag carries both hosts and
                // picks at runtime. Every `itp.*` piece is inert for other domains,
                // which have no client website domain, leaving the snippet unchanged.
                const itp = safariItpTagParts({
                    autDomain: hostname,
                    clientWebsiteDomain: this.safariItpClientDomain,
                    pathPrefix: this.safariItpPathPrefix,
                });
                template = `<script title="pxsnpt">\n`;
                if (itp.active) {
                    template += `  ${itp.detectionStatement}\n`;
                }
                template += `\
  var ${clientLabel}Data = {\n\
    cdmn : ${itp.cdmnExpr},\n\
    lm : '${loggingMode}',\n\
    tt : '${tagType}'${itp.active ? `,\n    pathPrefix : ${itp.pathPrefixValueExpr}` : ''}\n\
  };\n\
  !function(a){\n`;
                for (const key in tagParams) {
                    template += `    a.${key} = ${tagParams[key].toString().startsWith("!var:") ? tagParams[key].slice(5) : "'" + tagParams[key] + "'"};\n`;
                }
                template +=
                    `  }(${clientLabel}Data);\n\
  function _pxTagInject(p,d,w,l){\n\
    var o,k=[],t=p.tt.slice(-1),x={${itp.excludeKeys}},y={cid:1,cls:1,dmn:1,pubid:1,evid:1,aq_m:1};p.dmn=(p.dmn||(w.top[l]===w[l]?''+w[l]:d.referrer).split('/')[2]).split(':')[0];for(o in p){if(y[o]||((t!='j'||p.aq_m)&&!x[o])){k.push(o+"="+p[o]);};};var s=d.createElement(t=='f'?'img':'script');s.id=s.title='pxscrpt';s.async=s.defer=!0;s.src='//'+${itp.srcHostExpr}+'/1/'+p.lm+'/'+p.tt+'?'+k.join("&");d.body.appendChild(s);\n\
}_pxTagInject(${clientLabel}Data,document,window,'location');\n\
</` + `script>`;
            }

            return template;
        },

        calculatedTagType() {
            let tagType;
            if (this.deliveryType == "container") {
                tagType = "t" + this.logicType + ".dhj";
            } else {
                if (this.atomicType == "img") {
                    tagType = this.logicType == "c" ? "c.gif" : "a.gif";
                } else if (this.atomicType == "js") {
                    tagType = this.logicType + ".js";
                } else {
                    tagType = "r";
                }
            }
            return tagType;
        },
        cookieDomainOptions() {
            return this.domainList?.domains?.items.map((item) => { return { label: item.name, value: item.name } }) || []
        },
        tagDomainValue() {
            return this.tagDomain === '' ? '{{hostname}}' : this.tagDomain;
        },
        safariItpAllowed() {
            return isProxyEnabledDomain(this.domainList, this.tagDomain);
        },
        /** Click events are delivered as a redirect regardless of the calculated type. */
        effectiveTagType() {
            return this.eventType === 'clk' ? 'r' : this.calculatedTagType;
        },
        /** Image and redirect tags are a bare URL; everything else is a script wrapper. */
        emitsScript() {
            const tagType = this.effectiveTagType || '';
            return tagType.indexOf('.gif') === -1 && tagType !== 'r';
        },
        /**
         * Safari detection has to run in the browser, so it needs the script wrapper.
         * A bare URL cannot pick a domain per browser, so asking for the two values
         * on an image or redirect tag would collect input we then discard.
         */
        safariItpApplies() {
            return this.safariItpAllowed && this.emitsScript;
        },
        /**
         * Both values are required once Safari ITP applies. Until they are in, the tag
         * would be generated against the aUT domain only - showing that is worse than
         * showing nothing, because it is copyable and silently missing Safari routing.
         */
        safariItpIncomplete() {
            if (!this.safariItpApplies) return false;
            return !String(this.safariItpClientDomain || '').trim()
                || !String(this.safariItpPathPrefix || '').trim();
        },
        /** Domain/subdomain used in diagram, alias, cname: only updates after user clicks Generate */
        effectiveDomainName() {
            return (this.displayDomainName)
                ? this.formData.domainName?.trim() : 'example.com';
        },
        effectiveTagSubdomain() {
            return (this.displayDomainName)
                ? this.formData.tagSubdomain?.trim() : 'tag';
        },
        aliasRecordValue() {
            const domain = this.effectiveDomainName;
            const subdomain = this.effectiveTagSubdomain;
            const edgeHostname = this.domainList.edgeHostname || 'e1.a00000-cXXX.edgekey.net';
            const method = this.formData.dnsMethod;

            if (method === 'chaining') {
                // ANAME: tag1.example.com → edge hostname
                return `${subdomain}1.${domain} → ${edgeHostname}`;
            } else if (method === 'cloudflare-dns' || method === 'aws-route53') {
                // ALIAS: tag1.example.com → cloudfront distribution
                return `${subdomain}1.${domain} → ${edgeHostname}`;
            } else {
                return `${subdomain}.${domain} → ${subdomain}1.${domain}`;
            }
        },

        cnameRecordValue() {
            const domain = this.effectiveDomainName;
            const subdomain = this.effectiveTagSubdomain;
            const taggingUrl = `${subdomain}.${domain}`;
            const edgeHostname = this.domainList.edgeHostname || 'e1.a00000-cXXX.edgekey.net';
            const method = this.formData.dnsMethod;

            if (method === 'chaining' || method === 'cloudflare-dns' || method === 'aws-route53') {
                // CNAME: tag.example.com → tag1.example.com
                return `${taggingUrl} → ${subdomain}1.${domain}`;
            } else {
                // Direct: tag.example.com → edge hostname
                return `${taggingUrl} → ${edgeHostname}`;
            }
        },
        domainName() {
            if (this.formData.domainName.length > 0 && this.formData.tagSubdomain.length > 0)
                return this.formData.tagSubdomain + "." + this.formData.domainName
            else
                return null

        },
        /** Sample tag page: https://{tagging-host}/samples/{cid}.html — saved domain from domainList when present, else Section 2 form */
        sampleTagFirePageUrl() {
            const cidVal = this.cid != null && this.cid !== '' ? String(this.cid).trim() : '';
            if (!cidVal) return '';
            const items = this.domainList?.domains?.items;
            let host = '';
            if (items?.length && items[0]?.name) {
                host = String(items[0].name).trim().replace(/^https?:\/\//i, '').split('/')[0];
            }
            if (!host) {
                const domain = this.formData.domainName?.trim();
                const sub = this.formData.tagSubdomain?.trim();
                if (!domain || !sub) return '';
                host = `${sub}.${domain}`;
            }
            return `https://${host}/samples/${encodeURIComponent(cidVal)}.html`;
        },
        deploymentStatus() {
            return this.certDeploy?.deployment_status;
        },
        needActivation() {
            return !deepEqual(this.snapShot, this.generalSettings);
        },
        showRefreshButton() {
            return this.autdeploymentStatus && this.autdeploymentStatus.status !== '' && this.autdeploymentStatus.status !== 'COMPLETED'
        },
        enableActivateButton() {
            return (this.autdeploymentStatus && (this.autdeploymentStatus.status == '' || this.autdeploymentStatus.status == 'COMPLETED' 
                || this.autdeploymentStatus.status == 'ABORTED' || this.autdeploymentStatus.status == 'NOT_STARTED'))
        },
        privacySignalOptions() {
            const signals = this.privacySignals?.privacySignals;
            if (!signals) return [];
            return Object.keys(signals).map(key => ({ label: key, value: Number(key) }));
        },
        childSensitiveSignalOptions() {
            const signals = this.privacySignals?.childSensitiveDataSignals;
            if (!signals) return [];
            return Object.keys(signals).map(key => ({ label: key, value: Number(key) }));
        },
        sensitiveDataSignalOptions() {
            const signals = this.privacySignals?.sensitiveDataProcessingSignals;
            if (!signals) return [];
            return Object.keys(signals).map(key => ({ label: key, value: Number(key) }));
        },
        privacySignalInfo() {
            const signals = this.privacySignals?.privacySignals;
            if (!signals) return '';
            return Object.entries(signals).map(([k, v]) => `${k}. ${v}`).join('<br/>');
        },
        childSensitiveSignalInfo() {
            const signals = this.privacySignals?.childSensitiveDataSignals;
            if (!signals) return '';
            return Object.entries(signals).map(([k, v]) => `${k}. ${v}`).join('<br/>');
        },
        sensitiveDataSignalInfo() {
            const signals = this.privacySignals?.sensitiveDataProcessingSignals;
            if (!signals) return '';
            return Object.entries(signals).map(([k, v]) => `${k}. ${v}`).join('<br/>');
        },
        existingPrivacyConfigOptions() {
            if (!this.privacyConfigs || !Array.isArray(this.privacyConfigs)) return [];
            return this.privacyConfigs.map(c => ({ label: c.name, value: c.name }));
        },
        canProceedUsStates() {
            return this.privacyConfigSaved || !!this.usMultiStatePrivacy?.configName;
        },
        isCookieConfigEditable() {
            return this.$store.getters.userRoles.isAccessByRole([1, 1.5]);
        },
        showCookieDomainField() {
            return this.isCookieConfigEditable || !!this.generalSettings.cookieDomain;
        },
        showOptOutCookieNameField() {
            return this.isCookieConfigEditable || !!this.optoutCookie.name;
        },
        showOptOutCookieValueField() {
            return this.isCookieConfigEditable || !!this.optoutCookie.value;
        },
        isGppConfigEditable() {
            return this.$store.getters.userRoles.isAqferAdmin;
        },
        showTcfSetting() {
            return this.isGppConfigEditable || !!this.tcfVal;
        },
        showUsPrivacySetting() {
            return this.isGppConfigEditable || !!this.usMultiStatePrivacyVal;
        },
        showUsStatesForm() {
            // Non-admin roles (e.g. client_admin) can always create a Privacy Signal
            // Configuration via Save Config, even before an admin enables the US States toggle.
            return this.usMultiStatePrivacyVal || !this.isGppConfigEditable;
        },

    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            getDomainLists: "aut/getDomainLists",
            getDeploymentStatus: "aut/getCertificateDeploymentStatus",
            fetchInitiators: "aut/fetchAllInitiators",
            fetchInitiatorFields: "aut/fetchAllInitiatorFields",
            createTagTemplate: "aut/createTagTemplate",
            updateAutClientData: "aut/updateAutClientData",
            activateClientConfig: "aut/activateClientConfig",
            fetchCheckListStatus: "admin/fetchCheckListStatus",
            updateCheckListStatus: "admin/updateCheckListStatus",
            enableDisableClient: "admin/enableDisableClient",
            createCertificate: "aut/createCertificate",
            updateEdgeHostname: "aut/updateEdgeHostname",
            addPrivacyConfig: "aut/addPrivacyConfig",
        }),
        /** Additional log cookies must be explicit names; a lone '*' wildcard is not allowed in this list. */
        sanitizeCookieListEntries(list) {
            if (!Array.isArray(list)) return [];
            return list.filter((c) => String(c).trim() !== '*');
        },
        goBackToSection(sectionNumber) {
            Object.keys(this.openSections).forEach(key => {
                this.openSections[key] = false;
            });
            this.openSections[`section${sectionNumber}`] = true;
            this.currentSection = sectionNumber;
        },
        async continueToSection(sectionNumber) {
            try {
                await this.validateCurrentSection();

                // Only proceed to next section if validation succeeds
                Object.keys(this.openSections).forEach(key => {
                    this.openSections[key] = false;
                });
                this.openSections[`section${sectionNumber}`] = true;
                this.currentSection = sectionNumber;
            } catch (error) {
                let errorMessage;
                if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else {
                    errorMessage = error.message || "Unable to continue to next section. Please try again.";
                }
                this.$store.dispatch('addToast', {
                    message: errorMessage,
                    type: "error"
                });
                return;
            }
        },
        toggleSection(sectionNumber) {
            if (!this.canOpenSection(sectionNumber)) {
                const incompleteStep = this.getFirstIncompleteStep(sectionNumber);
                const stepNames = {
                    1: "Introduction",
                    2: "Edge Hostname and DNS",
                    3: "Cookies",
                    4: "Privacy Settings",
                    5: "Users, Alerts and Notifications",
                    6: "Activation",
                    7: "Data Collection, Logging & Tagging",
                    8: "Next Steps"
                };

                this.$store.dispatch("addToast", {
                    message: `Please complete "${stepNames[incompleteStep]}" (Step ${incompleteStep}) before accessing Step ${sectionNumber}`,
                    type: "warning"
                });
                return;
            }

            const sectionKey = `section${sectionNumber}`;
            this.openSections[sectionKey] = !this.openSections[sectionKey];

            if (this.openSections[sectionKey]) {
                this.currentSection = sectionNumber;
            }
        },
        canOpenSection(sectionNumber) {
            // Section 1 can always be opened
            if (sectionNumber === 1) {
                return true;
            }

            // Check if this step is already completed - completed steps can always be opened
            const currentStepKey = `step${sectionNumber}`;
            if (this.stepCompletion[currentStepKey]) {
                return true;
            }

            // For incomplete sections, check if all previous steps are completed
            for (let i = 1; i < sectionNumber; i++) {
                const stepKey = `step${i}`;
                if (!this.stepCompletion[stepKey]) {
                    return false;
                }
            }
            return true;
        },
        // Helper method to get the first incomplete step blocking a section
        getFirstIncompleteStep(targetSection) {
            for (let i = 1; i < targetSection; i++) {
                const stepKey = `step${i}`;
                if (!this.stepCompletion[stepKey]) {
                    return i;
                }
            }
            return targetSection - 1; // fallback
        },
        // Section validation methods
        validateSection2() {
            const domainName = this.formData.domainName;
            const tagSubdomain = this.formData.tagSubdomain;
            const dnsMethod = this.formData.dnsMethod;

            // Enable continue if CAA is found OR if domainName and tagSubdomain are entered
            this.step2Completed = !!(domainName && tagSubdomain && dnsMethod);
            if (this.caaFound && this.uploadedCertificate) {
                this.step2Completed = true;
            } else if (this.caaFound) {
                this.step2Completed = false;
            }
            this.updateTaggingUrl();
            this.updateDnsValues();
            return this.step2Completed;
        },
        onValidateAndContinue() {
            this.showModal({
                component: "DomainRefreshMethod",
                data: {
                    title: "Choose the validation method",
                    description: "Select the ownership validation method need to be used.",
                    cnameLabel: "DNS CNAME",
                    txtLabel: "DNS TXT",
                    onSelect: (method) => this.saveDomain(true, method),
                },
            });
        },
        async saveDomain(validate = false, validationMethod = null) {
            let ordinal = 0;
            let toastOptions = {}; 
            let domainList = JSON.parse(JSON.stringify(this.domainList))
            if (domainList.domains.items) {
                domainList.domains.items.map((domainObj) => {
                    if (ordinal < domainObj.ordinal)
                        ordinal = domainObj.ordinal;
                });
                ordinal++;
            }
            let newDomain = {
                name: this.domainName,
                status: "PENDING",
                ordinal: ordinal,
                forwardHost: null,
                recordType: this.recordType
            }
            if (validate) {
                const existing = domainList.domains.items.find((d) => d.name === this.domainName);
                if (existing) {
                    existing.recordType = this.recordType;
                }
            } else {
                domainList.domains.items.push(newDomain);
                this.displayDomainName= true;
            }
            try {
                const payload = { payload: domainList, validate };
                if (validate && validationMethod) {
                    payload.validationMethod = validationMethod === "DNS_TXT" ? "DNS_TXT" : null;
                }
                let response = await this.updateEdgeHostname(payload);
                const updatedDomainList = response?.data || domainList;
                toastOptions = {
                    message: "Added domain name successfully!",
                    type: "success",
                };
                if (validate) {
                    // 1. Identify domains that are not validated
                    const nonValidated = updatedDomainList?.domains?.items?.filter(
                        (item) => item?.status !== "VALIDATED"
                    );

                    if (nonValidated && nonValidated.length > 0) {
                        // 2. Build a structured error string that parseValidationError understands
                        // Format: "Domain: Error Message" separated by newlines
                        const errorList = nonValidated
                            .map(item => `${item.name}: Validation in progress. Kindly retry shortly`)
                            .join('\n');

                        throw new Error(`Invalid Input:\n${errorList}`);
                    } else {
                        await this.updateStepStatus(2);
                    }
                } else {
                    this.hideSection2GenerateBtn = true;
                }
                this.domainList = updatedDomainList;
            } catch (error) {
                const errMsg = error?.response?.data?.error ?? error?.message ?? 'Failed to save domain';
                const parsed = this.parseValidationError(errMsg);
                this.showModal({
                    component: 'ValidationErrorModal',
                    data: {
                        rawMessage: parsed.rawMessage,
                        parsedErrors: parsed.parsedErrors,
                    },
                });
                toastOptions = {
                    message: errMsg || "Something went wrong while saving the domain. Please try again.",
                    type: "error",
                };
            }
            if(!validate)
                this.$store.dispatch("addToast", toastOptions);
            if (toastOptions.type === "success") {
                this.fetchDomainList(this.domainName);
            }

        },
        parseValidationError(errorString) {
            const raw = typeof errorString === 'string' ? errorString : String(errorString || '');
            let text = raw.trim();
            if (text.toLowerCase().startsWith('invalid input:')) {
                text = text.slice('invalid input:'.length).trim();
            }
            const parsedErrors = [];
            const lines = text.split(/\n/).map((l) => l.trim()).filter(Boolean);
            for (const line of lines) {
                const colonIdx = line.indexOf(': ');
                let domain = '';
                let msgPart = line;
                if (colonIdx > 0) {
                    domain = line.slice(0, colonIdx).trim();
                    msgPart = line.slice(colonIdx + 2).trim();
                }
                const messages = msgPart.split(';').map((m) => m.trim()).filter(Boolean);
                if (messages.length) parsedErrors.push({ domain, messages });
                else if (line) parsedErrors.push({ domain: '', messages: [line] });
            }
            return { parsedErrors, rawMessage: raw };
        },
        formatValidationExpiry(expiry) {
            return expiry ? DateTimeFormat(expiry) : '';
        },
        async copyText(text, label = "Text") {
            if (!text) return;
            try {
                if (navigator.clipboard) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const input = document.createElement('textarea')
                    input.style = 'position: absolute; left: -1000px; top: -1000px';
                    input.value = text;
                    document.body.appendChild(input)
                    input.select()
                    input.setSelectionRange(0, 99999);
                    document.execCommand('copy')
                    document.body.removeChild(input)
                }
                this.$store.dispatch("addToast", {
                    message: `${label} copied to clipboard.`,
                    type: "info",
                });
            } catch (e) {
                this.$store.dispatch("addToast", {
                    message: `Failed to copy ${label}.`,
                    type: "danger",
                });
            }
        },
        async validateSectionSkip(sectionNumber) {
            await this.updateStepStatus(sectionNumber);
            return true;
        },

        async saveCookieSettings() {
            await this.saveAutSettings(3);
            return true;
        },
        async savePrivacySettings() {
            await this.saveAutSettings(4);
            return true;
        },
        async savePrivacyConfigInline() {
            this.privacyConfigLoading = true;
            const configName = this.privacyConfigName || `${this.cid}_default`;
            try {
                await this.addPrivacyConfig({
                    name: configName,
                    signals: { ...this.privacyConfigSignals }
                });
                await this.$store.dispatch("aut/fetchPrivacyConfig", this.cid);
                this.usMultiStatePrivacy.configName = configName;
                this.privacyConfigSaved = true;
                this.$store.dispatch('addToast', { message: `Privacy config "${configName}" saved successfully.`, type: 'success' });
            } catch (error) {
                const errMsg = error?.response?.data?.error || error.message || 'Failed to save privacy config';
                this.$store.dispatch('addToast', { message: errMsg, type: 'error' });
            } finally {
                this.privacyConfigLoading = false;
            }
        },
        selectExistingPrivacyConfig(configName) {
            this.usMultiStatePrivacy.configName = configName;
            this.privacyConfigSaved = true;
        },
        async saveDataCollectionSettings() {
            await this.saveAutSettings(7);
            return true;
        },

        getCurrentConfigSnapshot() {
            return {
                domainName: this.formData.domainName,
                tagSubdomain: this.formData.tagSubdomain,
                dnsMethod: this.formData.dnsMethod,

                cookieName: this.generalSettings.cookieName,
                cookieDomain: this.generalSettings.cookieDomain,

                tcf: JSON.stringify(this.tcf),
                usMultiStatePrivacy: JSON.stringify(this.usMultiStatePrivacy)
            };
        },

        // Check if activation is needed by comparing configurations
        isActivationNeeded() {
            if (!this.lastActivatedConfig) {
                // First time activation
                return true;
            }

            const currentConfig = this.getCurrentConfigSnapshot();

            // Compare configurations
            for (const key in currentConfig) {
                if (currentConfig[key] !== this.lastActivatedConfig[key]) {
                    return true;
                }
            }

            return false;
        },
        updateTaggingUrl() {
            if (this.domain) {
                // Update the Cookie Domain field if empty
                if (!this.generalSettings.cookieDomain) {
                    this.generalSettings.cookieDomain = this.domainName;
                }
                // Update DNS values
                this.updateDnsValues();
                // Update the Base URL
                this.updateBaseUrl();
            }
        },

        updateDnsValues() {
            this.showDnsInstructions(this.formData.dnsMethod);
        },

        showDnsInstructions(method) {
            // Show both ALIAS and CNAME for chaining methods
            if (method === 'chaining' || method === 'aws-route53' || method === 'cloudflare-dns') {
                this.showAliasRecord = true;
                this.showCnameRecord = true;
            } else {
                // Show only CNAME for other methods (cname-only, cloudflare-hosted)
                this.showAliasRecord = false;
                this.showCnameRecord = true;
            }
        },

        updateBaseUrl() {
            const domainName = this.formData.domainName;
            const tagSubdomain = this.formData.tagSubdomain;
            const loggingMode = this.calculateLoggingMode;

            if (domainName && tagSubdomain) {
                const baseUrl = `https://${tagSubdomain}.${domainName}/1/${loggingMode.toLowerCase()}/`;
                const baseUrlValue = document.getElementById('baseUrlValue');

                if (baseUrlValue) {
                    baseUrlValue.textContent = baseUrl;
                }
            }
        },
        async copyGeneratedCode() {
            let code = this.generatedCode;
            code = code.replaceAll("\n", "");
            code = code.replaceAll("  ", "");
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(code)
            } else {
                const input = document.createElement('textarea')
                input.style = 'position: absolute; left: -1000px; top: -1000px';
                input.value = code;
                document.body.appendChild(input)
                input.select()
                input.setSelectionRange(0, 99999);
                document.execCommand('copy')
                document.body.removeChild(input)
            }
            const options = {
                message: "Code copied to clipboard",
                type: "success",
            };
            this.$store.dispatch("addToast", options);
        },

        getBaseUrl() {
            const domainName = this.formData.domainName || 'example.com';
            const tagSubdomain = this.formData.tagSubdomain || 'tag';
            const loggingMode = this.calculateLoggingMode;

            return `https://${tagSubdomain}.${domainName}/1/${loggingMode.toLowerCase()}/`;
        },

        isValidEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        },

        validateCurrentSection() {
            switch (this.currentSection) {
                case 1: return this.validateSectionSkip(1);
                case 2: return this.validateSectionSkip();
                case 3: return this.saveCookieSettings();
                case 4: return this.savePrivacySettings();
                case 5: return this.validateSectionSkip(5);
                case 6: return this.validateSectionSkip(6);
                case 7: return this.saveDataCollectionSettings();
                case 8: return this.validateSectionSkip(8);
                default: return true;
            }
        },

        // Tag Template Modal Methods
        openTagModal() {
            this.showModal({
                component: "SaveTagModal",
                data: {
                    onSave: this.saveTagTemplate
                }
            });
        },

        async finish() {
            this.stepCompletion["step8"] = true;
            this.openSections["section8"] = false;
            await this.updateStepStatus(8)
            let toastoptions = {
                message: "Client settings updated successfully.",
                type: "success"
            }
            this.$store.dispatch('addToast', toastoptions);
        },
        clearSafariItpIfUnused() {
            if (this.safariItpApplies) return;
            this.safariItpClientDomain = '';
            this.safariItpPathPrefix = '';
        },
        async fetchDomainList(domainNameToSelect = null) {
            await this.getDomainLists().then((response) => {
                const list = response.data;
                if (!list || !Array.isArray(list) || !list.length) return;

                if (domainNameToSelect) {
                    for (const edgeHost of list) {
                        const items = edgeHost?.domains?.items;
                        if (items && Array.isArray(items)) {
                            const domainItem = items.find((d) => (d.name || '').trim() === (domainNameToSelect || '').trim());
                            if (domainItem) {
                                this.domainList = edgeHost;
                                if (!this.domainList.domains?.items) this.domainList.domains = { items: [] };
                                this.domainValidationDetails = domainItem.validationDetails ?? domainItem.validation_details ?? null;
                                return;
                            }
                        }
                    }
                }

                const fallback = list.find((h) => h?.domains?.items?.length) || list[0];
                this.domainList = fallback || this.domainList;
                if (!this.domainList.domains?.items) this.domainList.domains = { items: [] };
                this.domainValidationDetails = null;
            }).catch((err) => {
                console.error(err);
            });
        },
        initializeAutSettings() {
            this.generalSettings = this.autClientData
                ? JSON.parse(JSON.stringify(this.autClientData))
                : this.generalSettings;
            this.generalSettings.cookieList = this.sanitizeCookieListEntries(
                this.autClientData?.log_cookies?.client || []
            );
            this.fetchDeploymentData()
        },
        showDeleteUsers(rows) {
            this.showModal({
                component: "deleteUsers",
                data: { rows: rows, onDeleteUser: this.deleteUserInList }
            });
        },
        openAddUserModal() {
            this.showModal({
                component: 'addUser',
                data: { checkList: true, onAddUser: this.addUserToList }
            });
        },
        addUserToList(user, alert, notification) {
            // Add user to list
            const userCopy = JSON.parse(JSON.stringify(user));
            userCopy.alert = alert;
            userCopy.notification = notification;
            this.users = [...this.users, userCopy];
        },
        deleteUserInList(user) {
            // Remove user from list
            this.users = this.users.filter(u => u.email !== user.email);
        },
        updateAddUserButtonPlacement(selectedRowCount) {
            this.moveAddUserButton = selectedRowCount > 0;
        },
        async onGenerateCSR() {
            this.creating = true;
            let toastOptions;
            let certificate = {
                commonName: this.commonName,
                sans: [this.domainName],
                admin: { ...this.admin, phone: this.admin.phone.toString() },
                org: { ...this.org, phone: this.admin.phone.toString() },
            }
            await this.createCertificate(certificate).then((response) => {
                toastOptions = {
                    message: "Certificate created successfully.",
                    type: "success"
                }
                this.startDeploymentPolling(3);
                this.csrGenerated = true;
            }).catch(err => {
                let error = null
                if (err.response)
                    error = err.response.data.error;
                else error = err.message;
                toastOptions = {
                    message: error || "Something went wrong while creating certificate. Please contact support.",
                    type: "error"
                }
            })
            this.creating = false;
            this.$store.dispatch("addToast", toastOptions);
        },
        onCertificateUploadSuccess() {
            this.uploadedCertificate = true;
            this.validateSection2();
        },
        async readCertificateDeployment() {
            await this.getDeploymentStatus(this.commonName)
                .then(response => {
                    this.certDeploy = response.data;
                }).catch(error => {
                    this.certDeploy = null;
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                })
        },
        startDeploymentPolling(maxCount) {
            this.certDeploy = null;
            if (this.intervalId) { // clear any existing interval
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
            this.intervalCount = 0; // reset interval count

            // make initial call
            this.readCertificateDeployment();
            if (this.deploymentStatus === 'wait-upload-third-party')
                return;
            // start polling
            this.intervalId = setInterval(() => {
                if (this.intervalCount >= maxCount) {
                    clearInterval(this.intervalId);
                    this.intervalId = null;
                    return;
                }

                this.readCertificateDeployment();
                this.intervalCount++;
            }, 10000); // update deployment status every 10s for maxCount times
        },
        onFileUpload(name, event) {
            let inputFiles = event.target.files;

            if (inputFiles && inputFiles.length) {
                const file = inputFiles[0];
                const reader = new FileReader();

                reader.onload = (e) => {
                    this[name] = e.target.result;
                };

                reader.onerror = (error) => {
                    this[name] = 'Error reading file. Please try again.';
                };

                reader.readAsText(file);
            } else {
                // Clear content if no file selected
                this[name] = '';
            }
        },
        async saveTagTemplate(templateName) {
            let toastoptions
            try {
                const tagTemplate = {
                    "owner": this.ownerEmail,
                    "cid": this.cid,
                    "name": templateName,
                    "event_type": this.eventType,
                    "sub_event_type_1": this.subEventType1,
                    "sub_event_type_2": this.subEventType2,
                    "initiator": this.initiator,
                    "tag_type": this.calculatedTagType,
                    "logging_mode": this.calculateLoggingMode,
                    "tag": ""
                }
                await this.createTagTemplate(tagTemplate)
                toastoptions = {
                    message: "Tag Template " + templateName + " created successfully",
                    type: "success",
                };
            } catch (error) {
                if (error.response.data.error)
                    this.error = error.response.data.error
                else
                    this.error = error.message
                toastoptions = {
                    message: this.error || "Something went wrong. Please contact support.",
                    type: "error"
                }
                return
            } finally {
                this.eventType = ''
                this.$store.dispatch('addToast', toastoptions);
            }

        },

        async saveAutSettings(sectionNumber) {
            let toastoptions;
            try {
                this.generalSettings.optoutCookie = this.optoutCookie ? this.optoutCookie : {};
                this.generalSettings.gpp.tcf = this.tcf;
                // Validate gvlId: null when TCF is DISABLED or gvlId is empty
                if (this.generalSettings.gpp.tcf) {
                    this.generalSettings.gpp.tcf.gvlId = (this.generalSettings.gpp.tcf.status === "DISABLED" || this.generalSettings.gpp.tcf.gvlId === '') ? null : Number(this.generalSettings.gpp.tcf.gvlId);
                }
                this.generalSettings.gpp.usMultiStatePrivacy = this.usMultiStatePrivacy;
                this.generalSettings.log_cookies.client = this.generalSettings.cookieList;

                delete this.generalSettings.cookieList;
                const response = await this.updateAutClientData(this.generalSettings);
                await this.$store.dispatch('aut/fetchAutClientData', this.cid)
                await this.updateStepStatus(sectionNumber);
                this.initializeAutSettings();
            } catch (error) {
                if (error.response && error.response.data.error)
                    this.error = error.response.data.error;
                else this.error = error.message;

                toastoptions = {
                    message: this.error || "Something went wrong. Please contact support.",
                    type: "error"
                }

                this.$store.dispatch('addToast', toastoptions);

                // Throw error to prevent navigation to next step
                throw new Error(this.error || "Failed to save settings");
            }
        },
        async saveActivationSettings() {
            let toastoptions;

            try {

                // Enable client if not already enabled
                if (!this.generalSettings?.enabled) {
                    await this.enableDisableClient({ cid: this.cid, enabled: true });
                }

                // Activate the client
                await this.activateClient();

                // Store current configuration as last activated
                this.lastActivatedConfig = this.getCurrentConfigSnapshot();

                this.snapShot = JSON.parse(JSON.stringify(this.generalSettings));
                this.isActivated = true;
                toastoptions = {
                    message: "Client activated successfully with current configuration.",
                    type: "success"
                };
                this.$store.dispatch('addToast', toastoptions);

            } catch (error) {
                if (error.response?.data?.error)
                    this.error = error.response.data.error;
                else this.error = error.message;

                toastoptions = {
                    message: this.error || "Something went wrong while saving activation settings. Please contact support.",
                    type: "error"
                };
                this.$store.dispatch('addToast', toastoptions);

                // Throw error to prevent navigation to next step
                throw new Error(this.error || "Failed to save activation settings");
            }
        },
        async onRefreshClick() {
            //to show loading, clearing deployment status
            this.$store.dispatch("aut/setDeploymentStatus", null);
            this.$store.dispatch("aut/fetchDeploymentStatus");
        },
        async onActionBtnClick() {
            if (this.isActivating) return;
            this.isActivating = true;
            try {
                const activationSaveMethod = async () => {
                    try {
                        await this.saveActivationSettings();

                    } catch (error) {
                        console.error('Activation failed:', error);
                    }
                };

                this.showModal({
                    component: 'DiffViewModal',
                    data: {
                        prev: this.snapShot,
                        current: this.generalSettings,
                        saveMethod: activationSaveMethod,
                        modalTitle: "Update Config? (activate)",
                        actionBtnText: "Activate",
                        diffExists: this.needActivation
                    }
                });

            } catch (error) {
                console.error('Direct activation failed:', error);

                this.$store.dispatch('addToast', {
                    message: error.message || "Activation failed. Please try again.",
                    type: "error"
                });
            } finally {
                this.isActivating = false;
            }
        },
        async onActivateClick() {
            const activationSaveMethod = async () => {
                try {
                    await this.saveActivationSettings();
                    // Move to next section (section 7) only after successful activation
                    this.continueToSection(7);
                } catch (error) {
                    console.error('Activation failed:', error);
                }
            };

            this.showModal({
                component: 'DiffViewModal',
                data: {
                    prev: this.snapShot,
                    current: this.generalSettings,
                    saveMethod: activationSaveMethod,
                    modalTitle: "Update Config? (activate)",
                    actionBtnText: "Activate",
                    diffExists: this.needActivation
                }
            });
        },
        async activateClient() {
            let toastoptions;
            try {
                await this.activateClientConfig();
                toastoptions = {
                    message: "Activation submitted successfully.",
                    type: "success"
                }
                this.fetchDeploymentData();
            } catch (error) {
                if (error.response.data.error)
                    this.error = error.response.data.error;
                else this.error = error.message;
                toastoptions = {
                    message: this.error || "Something went wrong while activating deployment. Please contact support.",
                    type: "error"
                }
            }
            this.$store.dispatch('addToast', toastoptions);
        },
        async updateStepStatus(stepNo) {
            try {
                const step = "step" + stepNo
                const response = await this.updateCheckListStatus({
                    product_name: "TAG_MANAGER",
                    config: {
                        [step]: true
                    },
                    cid: this.cid
                });

                if (response && response.config && response.config.features) {
                    const tagManagerFeature = response.config.features.find(
                        feature => feature.name === "TAG_MANAGER"
                    );

                    if (tagManagerFeature && tagManagerFeature.checklist_steps) {
                        // Sync all steps with the server response
                        Object.keys(tagManagerFeature.checklist_steps).forEach(stepKey => {
                            if (stepKey in this.stepCompletion) {
                                this.stepCompletion[stepKey] = tagManagerFeature.checklist_steps[stepKey];
                            }
                        });
                    }
                } else {
                    this.stepCompletion[step] = true;
                }

            } catch (error) {
                if (error.response.data.error)
                    this.actError = error.response.data.error;
                else this.actError = error.message;
                let toastoptions = {
                    message: this.actError || "Something went wrong while updating step status. Please contact support.",
                    type: "error"
                }
                this.$store.dispatch('addToast', toastoptions);
            }
        },
        async checkCAA(domain) {
            try {
                // Response is null if no CAA records found or CAA records found with letsEncrypt
                await this.$store.dispatch('aut/checkCaaExitstence', domain).then((response) => {
                    this.caaFound = false;
                }).catch((error) => {
                    if (error.response.data.error.includes('CAA DNS record detected'))
                        this.caaFound = true;
                });
            } catch (error) {
                // Error will be thrown if CAA is found and letsEncrypt is not found so user should upload certificate
                if (error.response.data.error.includes('CAA DNS record detected'))
                    this.caaFound = true;
            } finally {
                this.uploadedCertificate = false;
                this.validateSection2();

            }
        },
        getValidation(name) {
            return [
                { type: 'required', errMsg: `${name} is required` }
            ]
        },
        async getInitiatorFields() {
            if (!this.initiator || this.initiator == '') return [];
            try {
                this.initiatorFields = await this.fetchInitiatorFields(
                    this.initiator
                );
            } catch (error) {
                return [];
            }
        },
        fetchDeploymentData() {
            this.$store.dispatch("aut/fetchDeploymentStatus");
            this.$store.dispatch("aut/fetchAutProdClientData");
        },
        formatStatusText(status) {
            const statusMap = {
                'IN_PROGRESS': 'In Progress',
                'NOT_STARTED': 'Not Started',
                'COMPLETED': 'Completed',
                'ABORTED': 'Aborted'
            };
            return statusMap[status] || status;
        },
    },

    async mounted() {
        await this.$store.dispatch('aut/fetchAutClientData', this.cid).then(async () => {
            this.$store.dispatch("aut/fetchCountries");
            this.$store.dispatch("aut/fetchPrivacyConfig", this.cid);
            this.$store.dispatch("aut/fetchPrivacySignals");
            this.initializeAutSettings();
            this.checkListStatus = await this.fetchCheckListStatus();

            // Sync stepCompletion with checkListStatus from API
            if (this.checkListStatus && this.checkListStatus.config && this.checkListStatus.config.features) {
                // Find TAG_MANAGER feature from the features array
                const tagManagerFeature = this.checkListStatus.config.features.find(
                    feature => feature.name === "TAG_MANAGER"
                );

                if (tagManagerFeature && tagManagerFeature.checklist_steps) {
                    // Sync stepCompletion with the checklist_steps
                    Object.keys(tagManagerFeature.checklist_steps).forEach(step => {
                        if (step in this.stepCompletion) {
                            this.stepCompletion[step] = tagManagerFeature.checklist_steps[step];
                        }
                    });

                    // If step 6 is completed, store current config as last activated
                    if (this.stepCompletion.step6) {
                        this.lastActivatedConfig = this.getCurrentConfigSnapshot();
                    }

                    // Find the first incomplete step and open that section
                    const incompleteStep = Object.keys(this.stepCompletion).find(step =>
                        this.stepCompletion[step] === false
                    );

                    if (incompleteStep) {
                        // Extract step number from 'step1', 'step2', etc.
                        const stepNumber = parseInt(incompleteStep.replace('step', ''));

                        // Open the section for the first incomplete step
                        this.openSections[`section${stepNumber}`] = true;
                        this.currentSection = stepNumber;
                    } else {
                        // If all steps are complete, open the last section
                        this.openSections.section8 = true;
                        this.currentSection = 8;
                    }
                }
            }
        });
        this.tcf = this.generalSettings.gpp?.tcf || this.tcf;
        this.usMultiStatePrivacy = this.generalSettings.gpp?.usMultiStatePrivacy || this.usMultiStatePrivacy;
        this.tcfVal = this.generalSettings?.gpp?.tcf?.status === 'ENABLED';
        this.optoutCookie = this.generalSettings?.optoutCookie || {};
        this.usMultiStatePrivacyVal = this.generalSettings?.gpp?.usMultiStatePrivacy?.configName ? true : false
        if (this.usMultiStatePrivacy?.configName) {
            this.privacyConfigSaved = true;
        }
        await this.fetchDomainList();
        await this.fetchInitiators();
        this.updateTaggingUrl();
        this.snapShot = JSON.parse(JSON.stringify(this.generalSettings))
        if (this.isInitialDataLoad) {
            this.$nextTick(() => {
                this.isInitialDataLoad = false;
            });
        }
    },

    beforeUnmount() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    },
};
</script>
<style scoped>
/* Container styling */
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

/* Header styling */
.header {
    text-align: center;
    margin-bottom: 30px;
}

.accordion-body {
    background-color: white;
}

.header h1 {
    color: #2c3e50;
    margin-bottom: 20px;
}

/* Progress bar styling */
.progress {
    height: 8px;
    background-color: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.progress-bar {
    transition: width 0.6s ease;
}

/* Accordion styling */
.accordion-button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 15px 20px;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    color: #495057;
    text-align: left;
    font-weight: 500;
}

.accordion-button:hover {
    background-color: #e9ecef;
}

.section-title {
    flex: 1;
}

/* Accordion Header Container */
.accordion-header-container {
    display: flex;
    align-items: center;
    width: 100%;
    position: relative;
    background-color: #ffffff;
    border: 1px solid #dee2e6;
    transition: background-color 0.15s ease-in-out;
}

.accordion-header-container:hover {
    background-color: #f8f9fa;
}

/* Step Indicator Styling - Professional Progress Design */
.step-indicator {
    display: flex;
    align-items: center;
    padding: 0 15px;
    flex-shrink: 0;
}

.step-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.3s ease;
    position: relative;
    border: 2px solid #dee2e6;
    background-color: #ffffff;
    color: #6c757d;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Incomplete step styling */
.step-circle:not(.completed) {
    border-color: #6c757d;
    background-color: #ffffff;
    color: #495057;
}

/* Completed step styling */
.step-circle.completed {
    border-color: #198754;
    background-color: #198754;
    color: white;
    animation: step-complete 0.4s ease;
}

/* Step number styling */
.step-number {
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
}

/* Checkmark styling */
.step-check {
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    position: relative;
    top: -1px;
}

/* Accordion button adjustments */
.accordion-header-container .accordion-button {
    flex: 1;
    margin: 0;
    border: none;
    border-radius: 0;
    background-color: transparent;
    color: inherit;
}

.accordion-header-container .accordion-button:hover {
    background-color: transparent;
}

.accordion-header-container .accordion-button:focus {
    background-color: transparent;
    box-shadow: none;
}

/* Accordion Arrow Icon */
.accordion-button::after {
    content: '';
    flex-shrink: 0;
    width: 1.25rem;
    height: 1.25rem;
    margin-left: auto;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23212529'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-size: 1.25rem;
    transition: transform 0.2s ease-in-out;
}

.accordion-button:not(.collapsed)::after {
    transform: rotate(-180deg);
}

.accordion-button.collapsed::after {
    transform: rotate(0deg);
}

/* Hover effects for step indicators */
.accordion-header-container:hover .step-circle:not(.completed) {
    border-color: #495057;
    background-color: #f8f9fa;
    color: #212529;
    transform: scale(1.02);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.accordion-header-container:hover .step-circle.completed {
    background-color: #157347;
    border-color: #157347;
    transform: scale(1.02);
    box-shadow: 0 2px 4px rgba(21, 115, 71, 0.3);
}

/* Animation for step completion */
@keyframes step-complete {
    0% {
        transform: scale(0.8);
        opacity: 0.7;
    }

    50% {
        transform: scale(1.1);
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}

/* Responsive design for smaller screens */
@media (max-width: 768px) {
    .step-circle {
        width: 28px;
        height: 28px;
        font-size: 12px;
    }

    .step-check {
        font-size: 14px;
    }
}


/* Highlight sections */
.highlight {
    background-color: #e7f3ff;
    border-left: 4px solid #0066cc;
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 4px;
}

.notes-section {
    background-color: #fff3cd;
    border-left: 4px solid #ffc107;
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 4px;
}

/* Button styling */
.btn {
    display: inline-block;
    font-weight: 400;
    line-height: 1.5;
    color: #212529;
    text-align: center;
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;
    user-select: none;
    border: 1px solid transparent;
    padding: 0.5rem 1rem;
    font-size: 1rem;
    border-radius: 0.375rem;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.btn-primary {
    color: #fff;
    background-color: #0d6efd;
    border-color: #0d6efd;
}

.btn-primary:hover {
    background-color: #0b5ed7;
    border-color: #0a58ca;
}

.btn-success {
    color: #fff;
}

/* Section navigation */
.section-navigation {
    display: flex;
    justify-content: space-between;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #dee2e6;
}

/* URL display styling */
.url-display {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 4px;
    margin-top: 15px;
}

.url-output {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 1.1em;
    font-weight: bold;
    color: #0066cc;
    background-color: #fff;
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    margin-top: 5px;
}

/* DNS info styling */

.dns-instructions {
    margin: 20px 0;
    padding: 20px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}


.dns-info-section {
    margin: 25px 0;
    padding: 20px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.dns-section-header {
    font-size: 1.2rem;
    font-weight: 600;
    color: #0e7490;
    margin: 20px 0 15px 0;
    padding-bottom: 8px;
    border-bottom: 2px solid #0ea5e9;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.dns-info-note {
    margin-bottom: 0.5rem;
    line-height: 1.45;
}

.dns-item {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    padding: 12px 16px;
    background: white;
    border-radius: 6px;
    border-left: 4px solid #0ea5e9;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.dns-item:hover {
    box-shadow: 0 4px 16px rgba(14, 165, 233, 0.15);
}

.dns-label {
    font-weight: 600;
    color: #334155;
    min-width: 140px;
    margin-right: 15px;
    font-size: 0.95rem;
}

.dns-value {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 1rem;
    color: #0ea5e9;
    font-weight: 500;
    background: #e0f2fe;
    padding: 6px 12px;
    border-radius: 4px;
    flex: 1;
    word-break: break-all;
}

.dns-value-with-action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.dns-value-with-action .dns-text {
    flex: 1;
    min-width: 0;
    word-break: break-all;
}

.dns-value-with-action .dns-action-btn {
    flex: 0 0 auto;
}

/* Validation details (checklist theme, CNameValidation-like layout) */
.validation-details-checklist .validation-record-block {
    margin-bottom: 1rem;
}

.validation-details-checklist .validation-record-block:last-of-type {
    margin-bottom: 0;
}

.validation-record-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    padding-left: 4px;
}

.validation-record-title {
    font-weight: 600;
    color: #334155;
    font-size: 0.95rem;
}

.validation-record-row {
    display: flex;
    align-items: stretch;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.validation-record-row .validation-record-field {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.validation-record-row .validation-record-label {
    min-width: 0;
    margin-right: 0;
    font-size: 0.85rem;
    color: #64748b;
}

.validation-record-row .dns-value {
    min-height: 2.25rem;
}

.validation-arrow {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    color: #0ea5e9;
    padding: 0 0.25rem;
}

@media (max-width: 991.98px) {
    .validation-record-row {
        flex-direction: column;
    }
    .validation-arrow {
        align-self: center;
        transform: rotate(90deg);
    }
}

/* Event Examples Styling */
.event-example-container {
    margin: 20px 0;
    padding: 20px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.event-name {
    font-size: 1.2rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 10px;
    border-bottom: 2px solid #e2e8f0;
    padding-bottom: 8px;
}

.event-description {
    margin: 15px 0;
    padding: 15px;
    background: #e0f2fe;
    border-left: 4px solid #0ea5e9;
    border-radius: 4px;
}

.event-description p {
    margin-bottom: 10px;
    color: #475569;
    line-height: 1.6;
}

.example-content {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    background: white;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 15px;
    color: #059669;
    word-break: break-all;
    font-size: 0.9rem;
    line-height: 1.4;
}

.javascript-code {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    background: #1e293b;
    color: #e2e8f0;
    border: 1px solid #475569;
    border-radius: 6px;
    padding: 15px;
    font-size: 0.85rem;
    line-height: 1.5;
    resize: vertical;
    width: 100%;
}

.container-note {
    margin-top: 10px;
    padding: 10px;
    background: #fef3c7;
    border-left: 4px solid #f59e0b;
    border-radius: 4px;
}

.container-note p {
    margin: 0;
    color: #92400e;
    font-weight: 500;
}

.no-events-message {
    text-align: center;
    color: #6b7280;
    font-style: italic;
    padding: 20px;
    background: #f9fafb;
    border-radius: 6px;
    border: 2px dashed #d1d5db;
}

/* Base URL Display Styling */
.base-url-display {
    margin: 25px 0;
    padding: 20px;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-radius: 8px;
    border: 1px solid #0ea5e9;
}

.base-url-label {
    font-weight: 600;
    color: #0c4a6e;
    margin-bottom: 10px;
    font-size: 1.1rem;
}

.base-url-value {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 1.2rem;
    font-weight: bold;
    color: #0ea5e9;
    background: white;
    padding: 12px 16px;
    border: 1px solid #0ea5e9;
    border-radius: 6px;
    word-break: break-all;
}

.completion-text {
    white-space: nowrap;
}

.completion-text .badge {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
}

/* Progress bar with same green as completed step checkmarks */
.progress-bar-step-green {
    background-color: #198754;
}

.extra-margin-end {
    margin-right: 90px;
}

.add-user-row {
    margin-top: 8px;
}

#addUserBtn {
    margin-bottom: 20px;
    height: 40.5px;
}

/* Styles for disabled accordion buttons */
.accordion-button:disabled,
.accordion-button.disabled {
    opacity: 0.6;
    cursor: not-allowed !important;
    background-color: #f8f9fa;
    color: #6c757d;
    pointer-events: auto;
    /* Allow pointer events for tooltip/message */
}

.accordion-button:disabled:hover,
.accordion-button.disabled:hover {
    background-color: #f8f9fa !important;
    color: #6c757d !important;
}

.accordion-button:disabled .section-title,
.accordion-button.disabled .section-title {
    color: #6c757d;
}

/* Step circle styling for incomplete steps */
.step-circle {
    transition: all 0.3s ease;
}

.accordion-button:disabled+.step-circle,
.accordion-button.disabled+.step-circle {
    opacity: 0.6;
}
</style>

<style lang="scss" scoped>
@use '../theme/scss/_user-variables.scss' as *;

body.dark-mode {

    .header h1 {
        color: $dark-text-primary;
    }

    .header p {
        color: $dark-text-secondary;
    }

    // Progress bar
    .progress {
        background-color: $dark-elevated-bg;
    }

    // Accordion items
    .accordion-item {
        background-color: $dark-card-bg;
        border-color: $dark-border-color;
    }

    .accordion-body {
        background-color: $dark-card-bg !important;
        color: $dark-text-primary;
    }

    // Accordion button (section headers)
    .accordion-button {
        background-color: $dark-card-bg;
        border-color: $dark-border-color;
        color: $dark-text-primary;

        &:hover {
            background-color: $dark-elevated-bg;
        }

        &:not(.collapsed) {
            background-color: $dark-elevated-bg;
            color: $dark-text-primary;
        }

        &::after {
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23a0a4b8'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
        }

        &:disabled, &.disabled {
            background-color: $dark-body-bg;
            color: $dark-text-muted;

            &:hover {
                background-color: $dark-body-bg !important;
                color: $dark-text-muted !important;
            }

            .section-title {
                color: $dark-text-muted;
            }
        }
    }

    // Accordion header container
    .accordion-header-container {
        background-color: $dark-card-bg;
        border-color: $dark-border-color;

        &:hover {
            background-color: $dark-elevated-bg;
        }

        .accordion-button {
            background-color: transparent;

            &:hover, &:focus {
                background-color: transparent;
            }
        }
    }

    // Step circles
    .step-circle {
        border-color: 0 2px 4px rgba(21, 115, 71, 0.4);
        background-color: 0 2px 4px rgba(21, 115, 71, 0.4);
        color: $dark-text-primary;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

        &:not(.completed) {
            border-color: $dark-text-muted;
            background-color: $dark-card-bg;
            color: $dark-text-secondary;
        }
    }

    .accordion-header-container:hover .step-circle:not(.completed) {
        border-color: $dark-text-secondary;
        background-color: $dark-elevated-bg;
        color: $dark-text-primary;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    .accordion-header-container:hover .step-circle.completed {
        box-shadow: 0 2px 4px rgba(21, 115, 71, 0.4);
    }

    // Highlight sections (blue info boxes)
    .highlight {
        background-color: rgba(44, 123, 229, 0.1);
        border-left-color: $dark-accent;
        color: $dark-text-primary;
    }

    // Notes sections (yellow)
    .notes-section {
        background-color: rgba(255, 193, 7, 0.1);
        border-left-color: #f59e0b;
        color: $dark-text-primary;
    }

    // Section navigation divider
    .section-navigation {
        border-top-color: $dark-border-color;
    }

    // URL display
    .url-display {
        background-color: $dark-elevated-bg;
    }

    .url-output {
        background-color: $dark-input-bg !important;
        color: lighten($dark-accent, 20%) !important;
        border-color: $dark-border-color;
    }

    // DNS info sections
    .dns-instructions {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
    }

    .dns-info-section {
        background: linear-gradient(135deg, $dark-elevated-bg 0%, $dark-card-bg 100%);
        border-color: $dark-border-color;
    }

    .dns-section-header {
        color: #38bdf8;
        border-bottom-color: #0ea5e9;
    }

    .dns-item {
        background: $dark-card-bg;
        border-left-color: #0ea5e9;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

        &:hover {
            box-shadow: 0 4px 16px rgba(14, 165, 233, 0.2);
        }
    }

    .dns-label {
        color: $dark-text-secondary;
    }

    .dns-value {
        color: #38bdf8;
        background: rgba(14, 165, 233, 0.1);
    }

    // Validation records
    .validation-record-title {
        color: $dark-text-primary;
    }

    .validation-record-row .validation-record-label {
        color: $dark-text-muted;
    }

    .validation-arrow {
        color: #38bdf8;
    }

    // Event example sections
    .event-example-container {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
    }

    .event-name {
        color: $dark-text-primary;
        border-bottom-color: $dark-border-color;
    }

    .event-description {
        background: rgba(14, 165, 233, 0.1);
        border-left-color: #0ea5e9;

        p {
            color: $dark-text-secondary;
        }
    }

    .example-content {
        background: $dark-input-bg;
        border-color: $dark-border-color;
        color: #34d399;
    }

    // Container note (yellow warning)
    .container-note {
        background: rgba(245, 158, 11, 0.1);
        border-left-color: #f59e0b;

        p {
            color: #fbbf24;
        }
    }

    // No events message
    .no-events-message {
        color: $dark-text-muted;
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
    }

    // Base URL display
    .base-url-display {
        background: linear-gradient(135deg, rgba(14, 165, 233, 0.08) 0%, rgba(14, 165, 233, 0.04) 100%);
        border-color: #0ea5e9;
    }

    .base-url-label {
        color: #38bdf8;
    }

    .base-url-value {
        color: #38bdf8;
        background: $dark-input-bg;
        border-color: rgba(14, 165, 233, 0.4);
    }

    // Cards inside accordion sections
    .accordion-body .card {
        background-color: $dark-card-bg !important;
        border-color: $dark-border-color;

        .card-header {
            background-color: $dark-elevated-bg;
            border-bottom-color: $dark-border-color;
            color: $dark-text-primary;

            h3, h4, h5 {
                color: $dark-text-primary;
            }
        }

        .card-body {
            background-color: $dark-card-bg;
            color: $dark-text-primary;
        }

        .card-footer {
            background-color: $dark-elevated-bg;
            border-top-color: $dark-border-color;
            color: $dark-text-secondary;

            code {
                color: #34d399;
            }
        }
    }

    // Headings
    .accordion-body {
        h3, h4, h5 {
            color: $dark-text-primary;
        }

        .form-text, .text-muted {
            color: $dark-text-muted !important;
        }

        .form-label {
            color: $dark-text-secondary;
        }
    }

    // Code examples (Section 8 sample queries)
    .code-example {
        background-color: $dark-input-bg;
        border-color: $dark-border-color;
        border-radius: 6px;

        code {
            color: #34d399;
        }

        pre {
            background-color: $dark-input-bg;
            color: $dark-text-primary;
            margin: 0;

            code {
                color: #34d399;
            }
        }
    }

    // Data lakehouse section
    .data-lakehouse-section {
        color: $dark-text-secondary;

        h3, h4 {
            color: $dark-text-primary;
        }
    }

    // GPP description
    .gpp-description {
        color: $dark-text-secondary;
    }

    // US states grid
    .us-states-grid .state-config {
        color: $dark-text-primary;
    }

    // CSR card section
    #csrContent {
        background-color: $dark-card-bg !important;
        border-color: $dark-border-color;
        color: $dark-text-primary;

        h3 {
            color: $dark-text-primary;
        }
    }

    // Form elements inside this component
    .form-control, .form-select {
        background-color: $dark-input-bg;
        border-color: $dark-border-color;
        color: $dark-text-primary;

        &:focus {
            background-color: $dark-elevated-bg;
            border-color: $dark-accent;
            color: $dark-text-primary;
        }

        &:disabled, &[readonly] {
            background-color: lighten($dark-body-bg, 4%);
            color: $dark-text-muted;
        }
    }

    // Radio and checkbox inputs
    .form-check-input {
        background-color: $dark-input-bg;
        border-color: $dark-border-color;
    }

    // Generic text overrides within the checklist scope
    #accordionChecklist {
        p, li {
            color: $dark-text-secondary;
        }

        strong {
            color: $dark-text-primary;
        }

        a:not(.btn) {
            color: lighten($dark-accent, 15%);
        }

        ul {
            color: $dark-text-secondary;
        }
    }
}
</style>